import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Home as HomeIcon, ListChecks, MessageCircle, User, Sparkles, Mic, Phone, MapPin, Truck, Star, ArrowRight, Signal, Wifi, Battery, Send, CreditCard, Settings, Shield, Bell, LogOut, CheckCircle2, Clock, Briefcase, Snowflake, Sofa, Package, Wrench, Navigation as NavigationIcon, ChevronLeft, Camera, ImagePlus, Loader2, History, Plus, AlertTriangle, Gavel, Receipt, Download, X, AlertCircle, Ban, Hourglass, XCircle, MessageSquare, Image as ImageIcon } from 'lucide-react';
import { detectServiceFromPrompt, getServiceConfig, ServiceId } from '../lib/serviceData';
import { getSlotsForService, parseInitialPrompt, parseUserReply, nextMissingSlot, formatAcknowledgement, getInitialGreeting, getCompletionMessage, Slot, SlotValues, ConversationTurn } from '../lib/agenticAgent';
export type MobileJobStatus = 'bidding_open' | 'pending_payment' | 'active' | 'completed' | 'cancelled';
export type MobileJobType = 'booking' | 'bidding';
export interface MobileJobBid {
  supplier: string;
  price: number;
  rating: number;
  initials: string;
  color: string;
  receivedAt: number;
  status?: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled';
  truck?: string;
}
export interface MobileJobRecord {
  id: string;
  type: MobileJobType;
  status: MobileJobStatus;
  serviceId: ServiceId;
  serviceName: string;
  prompt: string;
  routeHeadline: string;
  pickup?: string;
  dropoff?: string;
  supplierName?: string;
  supplierTruck?: string;
  price?: number;
  estimatedPrice?: number;
  createdAt: number;
  closesAt?: number;
  startedAt?: number;
  completedAt?: number;
  cancelledAt?: number;
  bids?: MobileJobBid[];
  rating?: number;
  reviewText?: string;
  ratingTags?: string[];
  ratedAt?: number;
  photos?: string[];
}
type ChatSession = {
  id: string;
  serviceId: ServiceId;
  prompt: string;
  createdAt: number;
  updatedAt: number;
  summary: string;
  lastMessage: string;
  lastFrom: 'ai' | 'user' | 'system';
  unread: boolean;
  status: 'active' | 'booked' | 'expired';
};
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
const MOCK_MOBILE_JOBS: MobileJobRecord[] = [{
  id: 'TRP-9921',
  type: 'booking',
  status: 'active',
  serviceId: 'home',
  serviceName: 'Apartment Move',
  prompt: 'Moving a 2-bedroom apartment from Marina to Downtown',
  routeHeadline: 'Dubai Marina → Downtown Dubai',
  pickup: 'Marina Heights, T2',
  dropoff: 'JLT, Cluster J',
  supplierName: 'FastTrack Logistics',
  supplierTruck: '3-Ton Box Truck',
  price: 420,
  createdAt: Date.now() - 4 * 60 * 60 * 1000,
  startedAt: Date.now() - 30 * 60 * 1000
}, {
  id: 'TRP-9922',
  type: 'booking',
  status: 'pending_payment',
  serviceId: 'furniture',
  serviceName: 'Furniture Delivery',
  prompt: 'Pick up a sofa and dining set from IKEA Festival City',
  routeHeadline: 'Festival City → Jumeirah Village Circle',
  pickup: 'IKEA Festival City',
  dropoff: 'Jumeirah Village Circle',
  supplierName: 'BlueLine Express',
  supplierTruck: '1.5-Ton Pickup',
  price: 320,
  createdAt: Date.now() - 25 * 60 * 1000
}, {
  id: 'TRP-9920',
  type: 'booking',
  status: 'completed',
  serviceId: 'furniture',
  serviceName: 'Furniture Delivery',
  prompt: 'Deliver a new mattress from Al Quoz to Marina',
  routeHeadline: 'Al Quoz → Dubai Marina',
  pickup: 'Al Quoz',
  dropoff: 'Dubai Marina',
  supplierName: 'Omar N.',
  supplierTruck: '1-Ton Pickup',
  price: 180,
  createdAt: Date.now() - 2 * 24 * 60 * 60 * 1000,
  completedAt: Date.now() - 1 * 24 * 60 * 60 * 1000,
  rating: 5,
  reviewText: 'Great service, very careful with the mattress.',
  ratingTags: ['On time', 'Careful with items'],
  ratedAt: Date.now() - 23 * 60 * 60 * 1000
}, {
  id: 'TRP-9919',
  type: 'booking',
  status: 'completed',
  serviceId: 'recovery',
  serviceName: 'Car Recovery',
  prompt: 'Recover my car from a parking accident in Business Bay',
  routeHeadline: 'Business Bay → Al Quoz garage',
  pickup: 'Business Bay',
  dropoff: 'Al Quoz garage',
  supplierName: 'Sahara Recovery',
  supplierTruck: 'Flatbed recovery truck',
  price: 250,
  createdAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
  completedAt: Date.now() - 4 * 24 * 60 * 60 * 1000
}, {
  id: 'TRP-9918',
  type: 'booking',
  status: 'cancelled',
  serviceId: 'equipment',
  serviceName: 'Heavy Equipment Hauling',
  prompt: 'Move an excavator from Al Quoz to Hatta site',
  routeHeadline: 'Al Quoz → Hatta',
  pickup: 'Al Quoz',
  dropoff: 'Hatta',
  supplierName: 'HeavyMove UAE',
  supplierTruck: 'Low-bed 40-Ton',
  price: 2400,
  createdAt: Date.now() - 10 * 24 * 60 * 60 * 1000,
  cancelledAt: Date.now() - 9 * 24 * 60 * 60 * 1000
}, {
  id: 'TRP-9923',
  type: 'bidding',
  status: 'bidding_open',
  serviceId: 'delivery',
  serviceName: 'Construction Materials',
  prompt: 'Need to move 12m steel beams from DIP to a tower site in Business Bay tomorrow morning',
  routeHeadline: 'Dubai Investments Park → Business Bay',
  pickup: 'Dubai Investments Park',
  dropoff: 'Business Bay',
  estimatedPrice: 1450,
  createdAt: Date.now() - 3 * 60 * 60 * 1000,
  closesAt: Date.now() + 21 * 60 * 60 * 1000,
  bids: [{
    supplier: 'Al Khaleej Movers',
    price: 1280,
    rating: 4.6,
    initials: 'AK',
    color: 'bg-amber-100 text-amber-700',
    receivedAt: Date.now() - 2 * 60 * 60 * 1000
  }, {
    supplier: 'BlueLine Express',
    price: 1190,
    rating: 4.8,
    initials: 'BL',
    color: 'bg-blue-100 text-blue-700',
    receivedAt: Date.now() - 90 * 60 * 1000
  }, {
    supplier: 'Sahara Logistics',
    price: 1240,
    rating: 4.7,
    initials: 'SL',
    color: 'bg-rose-100 text-rose-700',
    receivedAt: Date.now() - 45 * 60 * 1000
  }]
}, {
  id: 'TRP-9924',
  type: 'bidding',
  status: 'completed',
  serviceId: 'home',
  serviceName: 'Home Moving',
  prompt: 'Studio relocation from JLT to Silicon Oasis',
  routeHeadline: 'JLT → Dubai Silicon Oasis',
  pickup: 'JLT',
  dropoff: 'Dubai Silicon Oasis',
  supplierName: 'Sahara Logistics',
  supplierTruck: '2-Ton Box Truck',
  price: 620,
  estimatedPrice: 720,
  createdAt: Date.now() - 15 * 24 * 60 * 60 * 1000,
  completedAt: Date.now() - 14 * 24 * 60 * 60 * 1000,
  bids: [{
    supplier: 'Sahara Logistics',
    price: 620,
    rating: 4.7,
    initials: 'SL',
    color: 'bg-rose-100 text-rose-700',
    receivedAt: Date.now() - 15 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000,
    status: 'accepted'
  }]
}];
const MOCK_CHATS: ChatSession[] = [{
  id: 'chat-001',
  serviceId: 'home',
  prompt: 'Need to move a 3-bedroom apartment from Marina to JLT this weekend',
  createdAt: Date.now() - 25 * 60 * 1000,
  updatedAt: Date.now() - 3 * 60 * 1000,
  summary: 'Home move · Marina → JLT',
  lastMessage: '4 carriers sent quotes — Swift Movers offers AED 420',
  lastFrom: 'ai',
  unread: true,
  status: 'active'
}, {
  id: 'chat-002',
  serviceId: 'reefer',
  prompt: 'Refrigerated cargo, 2 pallets, frozen meat, -18°C',
  createdAt: Date.now() - 4 * 60 * 60 * 1000,
  updatedAt: Date.now() - 2 * 60 * 60 * 1000,
  summary: 'Refrigerated · 2 pallets · -18°C',
  lastMessage: 'You: Counter offer to Cool Chain Co.: AED 850',
  lastFrom: 'user',
  unread: false,
  status: 'active'
}, {
  id: 'chat-003',
  serviceId: 'furniture',
  prompt: 'Furniture delivery from IKEA Festival City to Downtown',
  createdAt: Date.now() - 18 * 60 * 60 * 1000,
  updatedAt: Date.now() - 18 * 60 * 60 * 1000,
  summary: 'Furniture · IKEA → Downtown',
  lastMessage: 'Booked with Omar N. · AED 180',
  lastFrom: 'system',
  unread: false,
  status: 'booked'
}, {
  id: 'chat-004',
  serviceId: 'recovery',
  prompt: 'Car broken down on SZR, need recovery to Al Quoz workshop',
  createdAt: Date.now() - 30 * 60 * 60 * 1000,
  updatedAt: Date.now() - 30 * 60 * 60 * 1000,
  summary: 'Recovery · SZR → Al Quoz',
  lastMessage: 'Quotes expired — start a new chat to rebook',
  lastFrom: 'system',
  unread: false,
  status: 'expired'
}];
type CarrierOffer = {
  id: string;
  name: string;
  rating: number;
  reviews: number;
  vehicle: string;
  price: number;
  eta: string;
  verified?: boolean;
  fast?: boolean;
};
const MOCK_OFFERS: CarrierOffer[] = [{
  id: 'off-1',
  name: 'Swift Movers LLC',
  rating: 4.9,
  reviews: 248,
  vehicle: '7-Ton Truck',
  price: 420,
  eta: '25 min',
  verified: true,
  fast: true
}, {
  id: 'off-2',
  name: 'Premier Logistics',
  rating: 4.8,
  reviews: 192,
  vehicle: '5-Ton Truck',
  price: 450,
  eta: '32 min',
  verified: true
}, {
  id: 'off-3',
  name: 'Falcon Express',
  rating: 4.7,
  reviews: 156,
  vehicle: '7-Ton Truck',
  price: 380,
  eta: '40 min'
}, {
  id: 'off-4',
  name: 'Desert Star Transport',
  rating: 4.6,
  reviews: 88,
  vehicle: '5-Ton Truck',
  price: 410,
  eta: '35 min'
}];
type BidEntry = {
  id: string;
  carrier: string;
  rating: number;
  price: number;
};
const BID_CARRIERS: Array<{
  name: string;
  rating: number;
}> = [{
  name: 'Rapid Cargo Co.',
  rating: 4.8
}, {
  name: 'BlueLine Transport',
  rating: 4.5
}, {
  name: 'Pioneer Movers',
  rating: 4.9
}, {
  name: 'GoldenHaul Freight',
  rating: 4.6
}, {
  name: 'Atlas Logistics',
  rating: 4.7
}, {
  name: 'Nova Cargo',
  rating: 4.4
}];
type OfferStage = 'offers' | 'counter-waiting' | 'counter-response' | 'bidding-live' | 'bidding-complete' | 'confirmed';
const SERVICES = [{
  id: 'recovery',
  label: 'Recovery',
  icon: Truck,
  color: 'red'
}, {
  id: 'home',
  label: 'Home Move',
  icon: HomeIcon,
  color: 'emerald'
}, {
  id: 'furniture',
  label: 'Furniture',
  icon: Sofa,
  color: 'sky'
}, {
  id: 'delivery',
  label: 'Delivery',
  icon: Package,
  color: 'amber'
}, {
  id: 'reefer',
  label: 'Refrigerated',
  icon: Snowflake,
  color: 'cyan'
}, {
  id: 'equipment',
  label: 'Heavy Equip.',
  icon: Wrench,
  color: 'fuchsia'
}] as const;
const COLOR_MAP: Record<string, string> = {
  red: 'bg-red-50 text-red-500',
  emerald: 'bg-emerald-50 text-emerald-500',
  sky: 'bg-sky-50 text-sky-500',
  amber: 'bg-amber-50 text-amber-600',
  cyan: 'bg-cyan-50 text-cyan-600',
  fuchsia: 'bg-fuchsia-50 text-fuchsia-600'
};
const DISCOVERY_CHIPS = [{
  label: 'Move home',
  icon: HomeIcon
}, {
  label: 'Furniture delivery',
  icon: Sofa
}, {
  label: 'Car recovery',
  icon: Truck
}, {
  label: 'Pallets / cargo',
  icon: Package
}, {
  label: 'Refrigerated load',
  icon: Snowflake
}, {
  label: 'Heavy equipment',
  icon: Wrench
}];
function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours < 1) {
    const mins = Math.max(1, Math.floor(diff / (60 * 1000)));
    return `${mins}m ago`;
  }
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
type ChatView = 'list' | 'new' | 'thread';
export function MobileApp() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'home' | 'trips' | 'chat' | 'profile'>('home');
  const [jobs, setJobs] = useState<MobileJobRecord[]>(MOCK_MOBILE_JOBS);
  const [chats, setChats] = useState<ChatSession[]>(MOCK_CHATS);
  const [chatView, setChatView] = useState<ChatView>('list');
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [activeService, setActiveService] = useState<ServiceId | null>(null);
  const [initialPrompt, setInitialPrompt] = useState<string>('');
  const activeChat = activeChatId ? chats.find(c => c.id === activeChatId) || null : null;
  const startNewChat = (serviceId: ServiceId, prompt: string) => {
    const newChat: ChatSession = {
      id: `chat-${Date.now()}`,
      serviceId,
      prompt,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      summary: prompt.slice(0, 60),
      lastMessage: prompt,
      lastFrom: 'user',
      unread: false,
      status: 'active'
    };
    setChats(prev => [newChat, ...prev]);
    setActiveChatId(newChat.id);
    setActiveService(serviceId);
    setInitialPrompt(prompt);
    setChatView('thread');
  };
  const handleStartChat = (serviceId?: ServiceId, prompt?: string) => {
    if (serviceId) {
      startNewChat(serviceId, prompt || '');
    } else {
      // From home AI prompt → open compose
      setActiveChatId(null);
      setActiveService(null);
      setInitialPrompt('');
      setChatView('new');
    }
    setTab('chat');
  };
  const handleDiscoverySubmit = (text: string) => {
    const detected = detectServiceFromPrompt(text);
    startNewChat(detected, text);
  };
  const handleComposeNewChat = () => {
    setActiveChatId(null);
    setActiveService(null);
    setInitialPrompt('');
    setChatView('new');
  };
  const handleBackToList = () => {
    setActiveChatId(null);
    setActiveService(null);
    setInitialPrompt('');
    setChatView('list');
  };
  const handleResumeChat = (chat: ChatSession) => {
    setActiveChatId(chat.id);
    setActiveService(chat.serviceId);
    setInitialPrompt(chat.prompt);
    setChatView('thread');
    // Clear unread when opening
    if (chat.unread) {
      setChats(prev => prev.map(c => c.id === chat.id ? {
        ...c,
        unread: false
      } : c));
    }
  };
  const handleChatBooked = (job: MobileJobRecord) => {
    setJobs(prev => [job, ...prev]);
    if (activeChatId) {
      setChats(prev => prev.map(c => c.id === activeChatId ? {
        ...c,
        status: 'booked',
        updatedAt: Date.now(),
        lastMessage: `Booked with ${job.supplierName || 'carrier'} · AED ${job.price}`,
        lastFrom: 'system'
      } : c));
    }
  };
  // When user taps Chat tab from bottom nav, default to list view
  useEffect(() => {
    if (tab !== 'chat') return;
    if (chatView === 'thread' && !activeChatId) setChatView('list');
  }, [tab, chatView, activeChatId]);
  return <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans" data-id="element-1610">
      <button onClick={() => navigate('/')} className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white hover:bg-gray-50 ring-1 ring-gray-200 text-gray-700 text-xs font-bold transition-colors shadow-sm" data-id="element-1611">
        <ChevronLeft className="w-3.5 h-3.5" data-id="element-1612" />
        All modules
      </button>
      <div className="w-[375px] h-[812px] bg-white rounded-[3rem] shadow-2xl overflow-hidden border-[10px] border-gray-900 relative flex flex-col" data-id="element-1613">
        <div className="h-11 w-full flex justify-between items-center px-7 pt-2 bg-white z-20 text-[13px] font-semibold shrink-0" data-id="element-1614">
          <span data-id="element-1615">9:41</span>
          <div className="w-32 h-7 bg-black rounded-full absolute left-1/2 -translate-x-1/2 top-2" data-id="element-1616"></div>
          <div className="flex items-center gap-1.5" data-id="element-1617">
            <Signal className="w-3.5 h-3.5" data-id="element-1618" />
            <Wifi className="w-3.5 h-3.5" data-id="element-1619" />
            <Battery className="w-4 h-4" data-id="element-1620" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-hide bg-gradient-to-b from-white to-gray-50" data-id="element-1621">
          <AnimatePresence mode="wait" data-id="element-1622">
            <motion.div key={tab} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.18
          }} className="h-full" data-id="element-1623">
              {tab === 'home' && <HomeTab jobs={jobs} onStartChat={(s, p) => handleStartChat(s, p)} data-id="element-1624" />}
              {tab === 'trips' && <TripsTab jobs={jobs} setJobs={setJobs} onStartChat={handleStartChat} data-id="element-1625" />}
              {tab === 'chat' && chatView === 'list' && <ChatThreadsList chats={chats} onResume={handleResumeChat} onCompose={handleComposeNewChat} data-id="element-1626" />}
              {tab === 'chat' && chatView === 'new' && <NewChatPrompt onSubmit={handleDiscoverySubmit} onBack={handleBackToList} data-id="element-1627" />}
              {tab === 'chat' && chatView === 'thread' && activeChat && activeService !== null && <AgenticChatTab key={activeChatId} chatId={activeChatId} serviceId={activeService} initialPrompt={initialPrompt} onBack={handleBackToList} onNewChat={handleComposeNewChat} onBook={handleChatBooked} data-id="element-1628" />}
              {tab === 'profile' && <ProfileTab onSignOut={() => navigate('/')} data-id="element-1629" />}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="shrink-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 px-6 pt-2 pb-6 flex justify-around" data-id="element-1630">
          {([{
          id: 'home',
          label: 'Home',
          icon: HomeIcon
        }, {
          id: 'trips',
          label: 'Trips',
          icon: ListChecks
        }, {
          id: 'chat',
          label: 'Chat',
          icon: MessageCircle
        }, {
          id: 'profile',
          label: 'Profile',
          icon: User
        }] as Array<{
          id: 'home' | 'trips' | 'chat' | 'profile';
          label: string;
          icon: typeof HomeIcon;
        }>).map(it => {
          const Icon = it.icon;
          const active = tab === it.id;
          return <button key={it.id} onClick={() => {
            setTab(it.id);
            if (it.id === 'chat' && chatView === 'new') {
              setChatView('list');
            }
          }} className="flex flex-col items-center gap-1 transition-colors" data-id="element-1631">
                <Icon className={`w-5 h-5 ${active ? 'text-brand-600' : 'text-gray-400'}`} data-id="element-1632" />
                <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? 'text-brand-600' : 'text-gray-400'}`} data-id="element-1633">
                  {it.label}
                </span>
              </button>;
        })}
        </div>
      </div>
    </div>;
}
function HomeTab({
  jobs,
  onStartChat
}: {
  jobs: MobileJobRecord[];
  onStartChat: (serviceId?: ServiceId, prompt?: string) => void;
}) {
  const active = jobs.find(t => t.status === 'active');
  return <div className="px-6 pt-4 pb-8 space-y-6" data-id="element-1634">
      <div className="flex items-center justify-between" data-id="element-1635">
        <div data-id="element-1636">
          <p className="text-xs text-gray-500 font-medium" data-id="element-1637">Good morning</p>
          <h1 className="text-2xl font-display font-bold text-gray-900 leading-tight" data-id="element-1638">
            Hi <span className="text-brand-500" data-id="element-1639">Sarah</span>
          </h1>
        </div>
        <button className="relative w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-1640">
          <Bell className="w-5 h-5 text-gray-700" data-id="element-1641" />
          <span className="absolute top-2 right-2 w-2 h-2 bg-brand-500 rounded-full" data-id="element-1642" />
        </button>
      </div>

      <FuturisticAiPrompt onStartChat={() => onStartChat()} data-id="element-1643" />

      {active && <div className="bg-brand-500 rounded-3xl p-5 text-white shadow-lg shadow-brand-500/20 relative overflow-hidden" data-id="element-1644">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-1645" />
          <div className="relative z-10" data-id="element-1646">
            <div className="flex items-center justify-between mb-3" data-id="element-1647">
              <span className="text-[10px] font-bold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full" data-id="element-1648">
                In progress
              </span>
              <span className="text-[10px] font-bold text-white/80" data-id="element-1649">
                {active.id}
              </span>
            </div>
            <h3 className="font-bold text-lg mb-1" data-id="element-1650">{active.serviceName}</h3>
            <p className="text-xs text-white/80 flex items-center gap-1.5 mb-4" data-id="element-1651">
              <MapPin className="w-3.5 h-3.5" data-id="element-1652" /> {active.dropoff}
            </p>
            <div className="flex items-center justify-between pt-3 border-t border-white/20" data-id="element-1653">
              <div className="flex items-center gap-2" data-id="element-1654">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold" data-id="element-1655">
                  {active.supplierName?.charAt(0) || 'D'}
                </div>
                <span className="text-xs font-medium" data-id="element-1656">
                  {active.supplierName || 'Driver'}
                </span>
              </div>
              <button className="text-[10px] font-bold bg-white text-brand-600 px-3 py-1.5 rounded-full" data-id="element-1657">
                Track
              </button>
            </div>
          </div>
        </div>}

      <div data-id="element-1658">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3" data-id="element-1659">
          Services
        </h2>
        <div className="grid grid-cols-3 gap-2" data-id="element-1660">
          {SERVICES.map(s => {
          const Icon = s.icon;
          return <button key={s.id} onClick={() => onStartChat(s.id as ServiceId)} className="bg-white border border-gray-100 rounded-2xl p-3 shadow-sm hover:shadow-md hover:border-gray-200 transition-all text-left" data-id="element-1661">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${COLOR_MAP[s.color]}`} data-id="element-1662">
                  <Icon className="w-4 h-4" data-id="element-1663" />
                </div>
                <p className="text-[11px] font-bold text-gray-900 leading-tight" data-id="element-1664">
                  {s.label}
                </p>
              </button>;
        })}
        </div>
      </div>

      <div className="bg-gradient-to-br from-brand-50 to-purple-50 border border-brand-100 rounded-2xl p-4 flex items-start gap-3" data-id="element-1665">
        <div className="w-8 h-8 rounded-lg bg-white text-brand-600 flex items-center justify-center shrink-0" data-id="element-1666">
          <Sparkles className="w-4 h-4" data-id="element-1667" />
        </div>
        <div data-id="element-1668">
          <p className="text-sm font-bold text-gray-900 leading-snug" data-id="element-1669">
            Open bidding saves you 18% on average.
          </p>
          <p className="text-xs text-gray-600 mt-0.5" data-id="element-1670">
            Let multiple carriers compete for your next move.
          </p>
        </div>
      </div>
    </div>;
}
function FuturisticAiPrompt({
  onStartChat
}: {
  onStartChat: () => void;
}) {
  const ROTATING = ['What do you want to move?', 'What service do you need?', 'Need a recovery truck?', 'Moving home or furniture?', 'Got cargo to transport?'];
  const [phraseIdx, setPhraseIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhraseIdx(i => (i + 1) % ROTATING.length), 2400);
    return () => clearInterval(t);
  }, []);
  return <button onClick={onStartChat} className="relative w-full text-left group" aria-label="Open AI assistant" data-id="element-1671">
      <motion.div animate={{
      background: ['linear-gradient(90deg, rgba(255,107,53,0.3), rgba(168,85,247,0.25), rgba(59,130,246,0.25), rgba(255,107,53,0.3))', 'linear-gradient(270deg, rgba(255,107,53,0.3), rgba(168,85,247,0.25), rgba(59,130,246,0.25), rgba(255,107,53,0.3))']
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute -inset-[1.5px] rounded-[1.4rem] blur-md opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-1672" />
      <div className="relative bg-white rounded-[1.3rem] p-4 flex items-center gap-3 border border-white shadow-lg shadow-gray-200/60 overflow-hidden" data-id="element-1673">
        <motion.div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-brand-400/10 blur-2xl" animate={{
        scale: [1, 1.3, 1],
        opacity: [0.5, 0.9, 0.5]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-1674" />
        <div className="relative shrink-0" data-id="element-1675">
          <motion.div animate={{
          boxShadow: ['0 0 0 0 rgba(255,107,53,0.4)', '0 0 0 8px rgba(255,107,53,0)']
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeOut'
        }} className="absolute inset-0 rounded-xl" data-id="element-1676" />
          <div className="relative w-11 h-11 rounded-xl orange-gradient-bg flex items-center justify-center" data-id="element-1677">
            <Sparkles className="w-5 h-5 text-white" data-id="element-1678" />
          </div>
        </div>
        <div className="flex-1 min-w-0 relative h-[40px] flex flex-col justify-center" data-id="element-1679">
          <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1680">
            <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-brand-600" data-id="element-1681">
              Pair2 AI
            </span>
            <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" data-id="element-1682" />
            <span className="text-[9px] font-medium text-gray-400" data-id="element-1683">
              ready to help
            </span>
          </div>
          <div className="relative h-5 overflow-hidden" data-id="element-1684">
            <AnimatePresence mode="wait" data-id="element-1685">
              <motion.p key={phraseIdx} initial={{
              y: 16,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} exit={{
              y: -16,
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} className="absolute inset-0 text-[13px] text-gray-900 font-semibold truncate" data-id="element-1686">
                {ROTATING[phraseIdx]}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
        <Mic className="w-5 h-5 text-gray-400 group-hover:text-brand-500 transition-colors shrink-0" data-id="element-1687" />
      </div>
    </button>;
}
function ActiveAction({
  icon: Icon,
  label
}: {
  icon: typeof Phone;
  label: string;
}) {
  return <button className="flex flex-col items-center gap-1 py-2 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition-colors" data-id="element-1688">
      <Icon className="w-4 h-4 text-white" data-id="element-1689" />
      <span className="text-[10px] font-bold text-white" data-id="element-1690">{label}</span>
    </button>;
}
type TabFilter = 'all' | 'active' | 'bidding' | 'completed' | 'cancelled';
function TripsTab({
  jobs,
  setJobs,
  onStartChat
}: {
  jobs: MobileJobRecord[];
  setJobs: React.Dispatch<React.SetStateAction<MobileJobRecord[]>>;
  onStartChat: () => void;
}) {
  const [filter, setFilter] = useState<TabFilter>('active');
  const [rateTarget, setRateTarget] = useState<MobileJobRecord | null>(null);
  const [receiptTarget, setReceiptTarget] = useState<MobileJobRecord | null>(null);
  const [trackingTarget, setTrackingTarget] = useState<MobileJobRecord | null>(null);
  const [bidsTarget, setBidsTarget] = useState<MobileJobRecord | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MobileJobRecord | null>(null);
  const unratedJob = jobs.filter(j => j.status === 'completed' && !j.rating).sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))[0];
  const filteredJobs = jobs.filter(j => {
    if (filter === 'all') return true;
    if (filter === 'active') return j.status === 'active' || j.status === 'pending_payment';
    if (filter === 'bidding') return j.type === 'bidding';
    if (filter === 'completed') return j.status === 'completed';
    if (filter === 'cancelled') return j.status === 'cancelled';
    return true;
  }).sort((a, b) => b.createdAt - a.createdAt);
  const counts = {
    all: jobs.length,
    active: jobs.filter(j => j.status === 'active' || j.status === 'pending_payment').length,
    bidding: jobs.filter(j => j.type === 'bidding').length,
    completed: jobs.filter(j => j.status === 'completed').length,
    cancelled: jobs.filter(j => j.status === 'cancelled').length
  };
  const handleRateSubmit = (jobId: string, stars: number, review: string, tags: string[]) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      rating: stars,
      reviewText: review,
      ratingTags: tags,
      ratedAt: Date.now()
    } : j));
  };
  const handleCancelJob = (jobId: string) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      status: 'cancelled',
      cancelledAt: Date.now(),
      bids: j.bids?.map(b => !b.status || b.status === 'pending' ? {
        ...b,
        status: 'cancelled'
      } : b)
    } : j));
  };
  const handleAcceptBid = (jobId: string, bidIndex: number) => {
    setJobs(prev => prev.map(j => {
      if (j.id !== jobId || !j.bids) return j;
      const accepted = j.bids[bidIndex];
      return {
        ...j,
        status: 'pending_payment',
        supplierName: accepted.supplier,
        supplierTruck: accepted.truck || 'Carrier-selected truck',
        price: accepted.price,
        bids: j.bids.map((b, i) => ({
          ...b,
          status: i === bidIndex ? 'accepted' : !b.status || b.status === 'pending' ? 'rejected' : b.status
        }))
      };
    }));
  };
  const handleRejectBid = (jobId: string, bidIndex: number) => {
    setJobs(prev => prev.map(j => {
      if (j.id !== jobId || !j.bids) return j;
      return {
        ...j,
        bids: j.bids.map((b, i) => ({
          ...b,
          status: i === bidIndex && (!b.status || b.status === 'pending') ? 'rejected' : b.status
        }))
      };
    }));
  };
  return <div className="flex flex-col h-full bg-gray-50" data-id="element-1691">
      <div className="px-6 pt-6 pb-4 bg-white border-b border-gray-100 shrink-0" data-id="element-1692">
        <div className="flex items-center justify-between mb-4" data-id="element-1693">
          <h1 className="text-2xl font-display font-bold text-gray-900 tracking-tight" data-id="element-1694">
            My Jobs
          </h1>
          <button onClick={onStartChat} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-full text-xs font-bold" data-id="element-1695">
            <Plus className="w-3.5 h-3.5" data-id="element-1696" />
            New
          </button>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1 -mx-2 px-2 scrollbar-hide" data-id="element-1697">
          {(['all', 'active', 'bidding', 'completed', 'cancelled'] as const).map(f => {
          const isActive = filter === f;
          return <button key={f} onClick={() => setFilter(f)} className={`relative px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors flex items-center gap-1.5 ${isActive ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1698">
                {isActive && <motion.div layoutId="trips-filter-pill" className="absolute inset-0 bg-gray-900 rounded-full" transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30
            }} data-id="element-1699" />}
                <span className="relative z-10 capitalize" data-id="element-1700">{f}</span>
                <span className={`relative z-10 text-[10px] px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-1701">
                  {counts[f]}
                </span>
              </button>;
        })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-id="element-1702">
        {unratedJob && <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between shadow-sm" data-id="element-1703">
            <div className="flex items-center gap-3" data-id="element-1704">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600" data-id="element-1705">
                <Star className="w-5 h-5 fill-current" data-id="element-1706" />
              </div>
              <div data-id="element-1707">
                <p className="text-xs font-bold text-amber-900 uppercase tracking-wider mb-0.5" data-id="element-1708">
                  Rate your trip
                </p>
                <p className="text-sm font-semibold text-amber-800" data-id="element-1709">
                  {unratedJob.supplierName}
                </p>
              </div>
            </div>
            <button onClick={() => setRateTarget(unratedJob)} className="px-4 py-2 bg-amber-500 text-white rounded-xl text-xs font-bold shadow-sm" data-id="element-1710">
              Rate now
            </button>
          </div>}

        <AnimatePresence mode="popLayout" data-id="element-1711">
          {filteredJobs.length === 0 ? <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="bg-white border border-gray-100 rounded-3xl p-8 text-center mt-4" data-id="element-1712">
              <div className="w-12 h-12 rounded-2xl bg-gray-50 text-gray-400 mx-auto mb-3 flex items-center justify-center" data-id="element-1713">
                <ListChecks className="w-6 h-6" data-id="element-1714" />
              </div>
              <p className="text-sm font-bold text-gray-900" data-id="element-1715">
                No {filter !== 'all' ? filter : ''} jobs found
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1716">
                When you book or request a service, it will appear here.
              </p>
            </motion.div> : filteredJobs.map((job, i) => <motion.div key={job.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          delay: i * 0.04
        }} data-id="element-1717">
                <MobileJobCard job={job} onRate={() => setRateTarget(job)} onReceipt={() => setReceiptTarget(job)} onTrack={() => setTrackingTarget(job)} onBids={() => setBidsTarget(job)} onCancel={() => setCancelTarget(job)} data-id="element-1718" />
              </motion.div>)}
        </AnimatePresence>
      </div>

      <AnimatePresence data-id="element-1719">
        {rateTarget && <RateSheet job={rateTarget} onClose={() => setRateTarget(null)} onSubmit={handleRateSubmit} data-id="element-1720" />}
        {receiptTarget && <ReceiptSheet job={receiptTarget} onClose={() => setReceiptTarget(null)} data-id="element-1721" />}
        {trackingTarget && <TrackingSheet job={trackingTarget} onClose={() => setTrackingTarget(null)} onCancel={() => {
        setTrackingTarget(null);
        setCancelTarget(trackingTarget);
      }} data-id="element-1722" />}
        {bidsTarget && <BidsSheet job={bidsTarget} onClose={() => setBidsTarget(null)} onAccept={handleAcceptBid} onReject={handleRejectBid} onCancel={() => {
        setBidsTarget(null);
        setCancelTarget(bidsTarget);
      }} data-id="element-1723" />}
        {cancelTarget && <CancelSheet job={cancelTarget} onClose={() => setCancelTarget(null)} onConfirm={() => {
        handleCancelJob(cancelTarget.id);
        setCancelTarget(null);
      }} data-id="element-1724" />}
      </AnimatePresence>
    </div>;
}
function MobileJobCard({
  job,
  onRate,
  onReceipt,
  onTrack,
  onBids,
  onCancel
}: {
  job: MobileJobRecord;
  onRate: () => void;
  onReceipt: () => void;
  onTrack: () => void;
  onBids: () => void;
  onCancel: () => void;
}) {
  const Icon = SERVICE_ICON_MAP[job.serviceId] || Package;
  const colorClass = SERVICE_COLOR_MAP[job.serviceId] || 'bg-gray-100 text-gray-600';
  const pillMap: Record<MobileJobStatus, string> = {
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    active: 'bg-amber-50 text-amber-700 border-amber-200',
    pending_payment: 'bg-blue-50 text-blue-700 border-blue-200',
    bidding_open: 'bg-brand-50 text-brand-700 border-brand-200',
    cancelled: 'bg-gray-50 text-gray-600 border-gray-200'
  };
  const pillLabel: Record<MobileJobStatus, string> = {
    completed: 'Completed',
    active: 'In progress',
    pending_payment: 'Pending payment',
    bidding_open: 'Bidding open',
    cancelled: 'Cancelled'
  };
  const created = new Date(job.createdAt);
  const isBiddingOpen = job.status === 'bidding_open';
  const closesIn = isBiddingOpen && job.closesAt ? Math.max(0, job.closesAt - Date.now()) : 0;
  const hoursLeft = Math.floor(closesIn / (60 * 60 * 1000));
  const pendingBids = job.bids?.filter(b => !b.status || b.status === 'pending') || [];
  const bestBid = pendingBids.length > 0 ? Math.min(...pendingBids.map(b => b.price)) : null;
  return <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-1725">
      <div className="p-4" data-id="element-1726">
        <div className="flex items-start justify-between mb-3" data-id="element-1727">
          <div className="flex items-center gap-3" data-id="element-1728">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${colorClass}`} data-id="element-1729">
              <Icon className="w-5 h-5" data-id="element-1730" />
            </div>
            <div data-id="element-1731">
              <div className="flex items-center gap-2 mb-0.5" data-id="element-1732">
                <h3 className="font-bold text-gray-900 text-sm" data-id="element-1733">
                  {job.serviceName}
                </h3>
                {job.type === 'bidding' && <span className="text-[9px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded" data-id="element-1734">
                    Bidding
                  </span>}
              </div>
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${pillMap[job.status]}`} data-id="element-1735">
                {job.status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" data-id="element-1736" />}
                {pillLabel[job.status]}
              </span>
            </div>
          </div>
          <div className="text-right" data-id="element-1737">
            {job.price ? <p className="font-display font-bold text-gray-900" data-id="element-1738">
                AED {job.price}
              </p> : job.estimatedPrice ? <p className="text-xs text-gray-500" data-id="element-1739">
                Est. AED {job.estimatedPrice}
              </p> : null}
            <p className="text-[10px] text-gray-400 font-medium" data-id="element-1740">
              {isBiddingOpen && hoursLeft > 0 ? `Closes in ${hoursLeft}h` : created.toLocaleDateString('en-AE', {
              month: 'short',
              day: 'numeric'
            })}
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-3 mb-3 space-y-2" data-id="element-1741">
          {job.pickup && <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-1742">
              <MapPin className="w-3.5 h-3.5 text-brand-500 shrink-0" data-id="element-1743" />
              <span className="truncate" data-id="element-1744">{job.pickup}</span>
            </div>}
          {job.dropoff && <div className="flex items-center gap-2 text-xs text-gray-700" data-id="element-1745">
              <MapPin className="w-3.5 h-3.5 text-emerald-500 shrink-0" data-id="element-1746" />
              <span className="truncate" data-id="element-1747">{job.dropoff}</span>
            </div>}
        </div>

        {job.supplierName && <div className="flex items-center gap-2 text-xs text-gray-500 mb-3 px-1" data-id="element-1748">
            <Truck className="w-3.5 h-3.5" data-id="element-1749" />
            <span className="font-medium text-gray-700" data-id="element-1750">
              {job.supplierName}
            </span>
            {job.supplierTruck && <span data-id="element-1751">• {job.supplierTruck}</span>}
          </div>}

        {isBiddingOpen && <div className="flex items-center justify-between bg-brand-50/50 border border-brand-100 rounded-xl p-2.5 mb-3" data-id="element-1752">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-brand-700" data-id="element-1753">
              <Gavel className="w-3.5 h-3.5" data-id="element-1754" />
              {pendingBids.length} live bids
            </div>
            {bestBid && <div className="text-xs text-brand-700" data-id="element-1755">
                Best: <span className="font-bold" data-id="element-1756">AED {bestBid}</span>
              </div>}
          </div>}

        {job.rating && <div className="flex items-center gap-1 mb-3 px-1" data-id="element-1757">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= job.rating! ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} data-id="element-1758" />)}
          </div>}

        <div className="flex gap-2 pt-3 border-t border-gray-100" data-id="element-1759">
          {job.status === 'active' && <>
              <button onClick={onCancel} className="px-4 py-2.5 text-xs font-bold text-gray-500 hover:bg-gray-50 rounded-xl" data-id="element-1760">
                Cancel
              </button>
              <button onClick={onTrack} className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5" data-id="element-1761">
                <NavigationIcon className="w-3.5 h-3.5" data-id="element-1762" /> Track live
              </button>
            </>}
          {job.status === 'pending_payment' && <>
              <button onClick={onCancel} className="px-4 py-2.5 text-xs font-bold text-gray-500 hover:bg-gray-50 rounded-xl" data-id="element-1763">
                Cancel
              </button>
              <button className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5" data-id="element-1764">
                <CreditCard className="w-3.5 h-3.5" data-id="element-1765" /> Pay now
              </button>
            </>}
          {job.status === 'completed' && !job.rating && <>
              <button onClick={onReceipt} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-xs font-bold" data-id="element-1766">
                View receipt
              </button>
              <button onClick={onRate} className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5" data-id="element-1767">
                <Star className="w-3.5 h-3.5" data-id="element-1768" /> Rate
              </button>
            </>}
          {job.status === 'completed' && job.rating && <button onClick={onReceipt} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5" data-id="element-1769">
              <Receipt className="w-3.5 h-3.5" data-id="element-1770" /> View receipt
            </button>}
          {job.status === 'bidding_open' && <>
              <button onClick={onCancel} className="px-4 py-2.5 text-xs font-bold text-gray-500 hover:bg-gray-50 rounded-xl" data-id="element-1771">
                Cancel
              </button>
              <button onClick={onBids} className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5" data-id="element-1772">
                <Gavel className="w-3.5 h-3.5" data-id="element-1773" /> View bids
              </button>
            </>}
          {job.status === 'cancelled' && <button className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-xs font-bold" data-id="element-1774">
              Book again
            </button>}
        </div>
      </div>
    </div>;
}
function BottomSheet({
  children,
  onClose
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return <div className="absolute inset-0 z-50 flex flex-col justify-end" data-id="element-1775">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-1776" />
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="relative bg-white rounded-t-3xl shadow-2xl max-h-[88%] flex flex-col overflow-hidden" data-id="element-1777">
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-gray-200 rounded-full" data-id="element-1778" />
        <div className="pt-8 flex-1 overflow-y-auto pb-safe" data-id="element-1779">{children}</div>
      </motion.div>
    </div>;
}
function RateSheet({
  job,
  onClose,
  onSubmit
}: {
  job: MobileJobRecord;
  onClose: () => void;
  onSubmit: (id: string, stars: number, review: string, tags: string[]) => void;
}) {
  const [stars, setStars] = useState(0);
  const [tags, setTags] = useState<string[]>([]);
  const [review, setReview] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const TAGS = ['On time', 'Professional', 'Friendly', 'Careful with items', 'Good communication', 'Clean truck', 'Would book again'];
  const handleSubmit = () => {
    if (!stars) return;
    onSubmit(job.id, stars, review, tags);
    setSubmitted(true);
    setTimeout(onClose, 1400);
  };
  if (submitted) {
    return <BottomSheet onClose={onClose} data-id="element-1780">
        <div className="p-8 text-center" data-id="element-1781">
          <motion.div initial={{
          scale: 0.5,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="w-16 h-16 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4" data-id="element-1782">
            <CheckCircle2 className="w-8 h-8" data-id="element-1783" />
          </motion.div>
          <h3 className="text-xl font-display font-bold text-gray-900 mb-1" data-id="element-1784">
            Thanks for rating!
          </h3>
          <p className="text-sm text-gray-500" data-id="element-1785">Your feedback helps others.</p>
        </div>
      </BottomSheet>;
  }
  return <BottomSheet onClose={onClose} data-id="element-1786">
      <div className="px-6 pb-6" data-id="element-1787">
        <div className="text-center mb-6" data-id="element-1788">
          <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-500 flex items-center justify-center mx-auto mb-3" data-id="element-1789">
            <Star className="w-6 h-6 fill-current" data-id="element-1790" />
          </div>
          <h3 className="text-xl font-display font-bold text-gray-900 mb-1" data-id="element-1791">
            How was your move?
          </h3>
          <p className="text-sm text-gray-500" data-id="element-1792">{job.supplierName}</p>
        </div>

        <div className="flex justify-center gap-2 mb-8" data-id="element-1793">
          {[1, 2, 3, 4, 5].map(s => <button key={s} onClick={() => setStars(s)} className="p-1 transition-transform active:scale-90" data-id="element-1794">
              <Star className={`w-10 h-10 ${s <= stars ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} data-id="element-1795" />
            </button>)}
        </div>

        <div className="mb-6" data-id="element-1796">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3" data-id="element-1797">
            What stood out?
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-1798">
            {TAGS.map(t => <button key={t} onClick={() => setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])} className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${tags.includes(t) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-100 bg-white text-gray-600'}`} data-id="element-1799">
                {t}
              </button>)}
          </div>
        </div>

        <div className="mb-6" data-id="element-1800">
          <textarea value={review} onChange={e => setReview(e.target.value)} rows={3} placeholder="Add a review (optional)..." className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none text-sm focus:border-brand-300 resize-none" data-id="element-1801" />
        </div>

        <button onClick={handleSubmit} disabled={!stars} className="w-full py-3.5 bg-gray-900 disabled:opacity-50 text-white rounded-xl font-bold text-sm" data-id="element-1802">
          Submit rating
        </button>
      </div>
    </BottomSheet>;
}
function ReceiptSheet({
  job,
  onClose
}: {
  job: MobileJobRecord;
  onClose: () => void;
}) {
  const total = job.price || 0;
  const vatRate = 0.05;
  const serviceRate = 0.05;
  const preVat = total / (1 + vatRate);
  const vat = Math.round(total - preVat);
  const subtotal = Math.round(preVat / (1 + serviceRate));
  const serviceFee = Math.round(preVat - subtotal);
  const invoiceNo = `INV-2026-${job.id.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(-6)}`;
  const issuedAt = job.completedAt || job.createdAt;
  return <BottomSheet onClose={onClose} data-id="element-1803">
      <div className="px-6 pb-6" data-id="element-1804">
        <div className="text-center mb-6 pb-6 border-b border-dashed border-gray-200" data-id="element-1805">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-100 mb-3" data-id="element-1806">
            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1807" />
            <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-1808">
              Paid
            </span>
          </div>
          <h2 className="text-2xl font-display font-black text-gray-900 mb-1" data-id="element-1809">
            Pair2 Move
          </h2>
          <p className="text-xs text-gray-500" data-id="element-1810">TRN AE100765432100</p>
        </div>

        <div className="space-y-4 mb-6" data-id="element-1811">
          <div className="flex justify-between text-sm" data-id="element-1812">
            <span className="text-gray-500" data-id="element-1813">Invoice no.</span>
            <span className="font-semibold text-gray-900" data-id="element-1814">{invoiceNo}</span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-1815">
            <span className="text-gray-500" data-id="element-1816">Issued</span>
            <span className="font-semibold text-gray-900" data-id="element-1817">
              {new Date(issuedAt).toLocaleDateString()}
            </span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-1818">
            <span className="text-gray-500" data-id="element-1819">Carrier</span>
            <span className="font-semibold text-gray-900" data-id="element-1820">
              {job.supplierName}
            </span>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-4 mb-6" data-id="element-1821">
          <p className="font-bold text-gray-900 text-sm mb-1" data-id="element-1822">
            {job.serviceName}
          </p>
          <p className="text-xs text-gray-500 mb-3" data-id="element-1823">{job.routeHeadline}</p>
          <div className="space-y-2 pt-3 border-t border-gray-200 text-sm" data-id="element-1824">
            <div className="flex justify-between" data-id="element-1825">
              <span className="text-gray-600" data-id="element-1826">Carrier fee</span>
              <span className="font-medium text-gray-900" data-id="element-1827">AED {subtotal}</span>
            </div>
            <div className="flex justify-between" data-id="element-1828">
              <span className="text-gray-600" data-id="element-1829">Service fee (5%)</span>
              <span className="font-medium text-gray-900" data-id="element-1830">
                AED {serviceFee}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-1831">
              <span className="text-gray-600" data-id="element-1832">VAT (5%)</span>
              <span className="font-medium text-gray-900" data-id="element-1833">AED {vat}</span>
            </div>
          </div>
          <div className="flex justify-between items-end pt-3 mt-3 border-t border-gray-200" data-id="element-1834">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-1835">
              Total paid
            </span>
            <span className="text-2xl font-display font-bold text-gray-900" data-id="element-1836">
              AED {total}
            </span>
          </div>
        </div>

        <div className="flex gap-3" data-id="element-1837">
          <button onClick={onClose} className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm" data-id="element-1838">
            Close
          </button>
          <button onClick={() => window.print()} className="flex-1 py-3.5 bg-gray-900 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2" data-id="element-1839">
            <Download className="w-4 h-4" data-id="element-1840" /> Save PDF
          </button>
        </div>
      </div>
    </BottomSheet>;
}
function TrackingSheet({
  job,
  onClose,
  onCancel
}: {
  job: MobileJobRecord;
  onClose: () => void;
  onCancel: () => void;
}) {
  const steps = ['Confirmed', 'Driver assigned', 'On the way', 'Delivered'];
  const activeStep = 2; // Mock 'On the way'
  return <BottomSheet onClose={onClose} data-id="element-1841">
      <div className="px-6 pb-6" data-id="element-1842">
        <div className="flex items-center justify-between mb-6" data-id="element-1843">
          <div data-id="element-1844">
            <h3 className="text-xl font-display font-bold text-gray-900" data-id="element-1845">
              Arriving in ~14 min
            </h3>
            <p className="text-sm text-gray-500" data-id="element-1846">4.2 km away</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold text-lg" data-id="element-1847">
            {job.supplierName?.charAt(0) || 'D'}
          </div>
        </div>

        <div className="relative h-48 bg-gray-100 rounded-2xl mb-6 overflow-hidden border border-gray-200" data-id="element-1848">
          {/* Mock Map Background */}
          <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(#000 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-1849" />
          {/* Mock Route Path */}
          <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-1850">
            <path d="M 20 80 Q 50 80 50 50 T 80 20" fill="none" stroke="#cbd5e1" strokeWidth="4" strokeDasharray="6 6" data-id="element-1851" />
            <motion.path d="M 20 80 Q 50 80 50 50 T 80 20" fill="none" stroke="#3b82f6" strokeWidth="4" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 0.6
          }} transition={{
            duration: 2,
            ease: 'easeOut'
          }} data-id="element-1852" />
          </svg>
          <div className="absolute bottom-4 left-4 w-4 h-4 bg-brand-500 rounded-full border-2 border-white shadow-md animate-pulse" data-id="element-1853" />
          <div className="absolute top-4 right-4 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white shadow-md" data-id="element-1854" />
          <motion.div className="absolute w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-100" initial={{
          bottom: '16%',
          left: '16%'
        }} animate={{
          bottom: '46%',
          left: '46%'
        }} transition={{
          duration: 2,
          ease: 'easeOut'
        }} data-id="element-1855">
            <Truck className="w-4 h-4 text-brand-600" data-id="element-1856" />
          </motion.div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-4 mb-6" data-id="element-1857">
          <div className="flex items-center justify-between mb-4" data-id="element-1858">
            <div data-id="element-1859">
              <p className="font-bold text-gray-900 text-sm" data-id="element-1860">
                {job.supplierName}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1861">
                {job.supplierTruck} • F-21934
              </p>
            </div>
            <div className="flex gap-2" data-id="element-1862">
              <button className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-700 shadow-sm" data-id="element-1863">
                <MessageCircle className="w-4 h-4" data-id="element-1864" />
              </button>
              <button className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-700 shadow-sm" data-id="element-1865">
                <Phone className="w-4 h-4" data-id="element-1866" />
              </button>
            </div>
          </div>
          <div className="space-y-4 relative before:absolute before:inset-0 before:ml-[11px] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gray-200" data-id="element-1867">
            {steps.map((step, i) => <div key={step} className="relative flex items-center gap-4" data-id="element-1868">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 border-2 ${i < activeStep ? 'bg-brand-500 border-brand-500 text-white' : i === activeStep ? 'bg-white border-brand-500 text-brand-500' : 'bg-white border-gray-300 text-transparent'}`} data-id="element-1869">
                  {i < activeStep && <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1870" />}
                  {i === activeStep && <div className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" data-id="element-1871" />}
                </div>
                <p className={`text-sm font-medium ${i <= activeStep ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-1872">
                  {step}
                </p>
              </div>)}
          </div>
        </div>

        <button onClick={onCancel} className="w-full py-3.5 text-rose-600 bg-rose-50 rounded-xl font-bold text-sm" data-id="element-1873">
          Cancel trip
        </button>
      </div>
    </BottomSheet>;
}
function BidsSheet({
  job,
  onClose,
  onAccept,
  onReject,
  onCancel
}: {
  job: MobileJobRecord;
  onClose: () => void;
  onAccept: (id: string, idx: number) => void;
  onReject: (id: string, idx: number) => void;
  onCancel: () => void;
}) {
  const bids = job.bids || [];
  const pending = bids.map((b, i) => ({
    ...b,
    originalIndex: i
  })).filter(b => !b.status || b.status === 'pending');
  const accepted = bids.map((b, i) => ({
    ...b,
    originalIndex: i
  })).filter(b => b.status === 'accepted');
  const rejected = bids.map((b, i) => ({
    ...b,
    originalIndex: i
  })).filter(b => b.status === 'rejected');
  const bestPending = pending.length ? pending.reduce((a, b) => a.price < b.price ? a : b) : null;
  return <BottomSheet onClose={onClose} data-id="element-1874">
      <div className="px-6 pb-6" data-id="element-1875">
        <div className="flex items-start gap-3 mb-6" data-id="element-1876">
          <div className="w-12 h-12 rounded-2xl bg-gray-900 text-white flex items-center justify-center shrink-0" data-id="element-1877">
            <Gavel className="w-6 h-6" data-id="element-1878" />
          </div>
          <div data-id="element-1879">
            <h3 className="text-lg font-display font-bold text-gray-900 leading-tight" data-id="element-1880">
              Bidding • {job.serviceName}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-1881">{job.routeHeadline}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-6" data-id="element-1882">
          <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100" data-id="element-1883">
            <p className="text-xl font-display font-bold text-amber-700" data-id="element-1884">
              {pending.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600" data-id="element-1885">
              Pending
            </p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100" data-id="element-1886">
            <p className="text-xl font-display font-bold text-green-700" data-id="element-1887">
              {accepted.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-green-600" data-id="element-1888">
              Accepted
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200" data-id="element-1889">
            <p className="text-xl font-display font-bold text-gray-700" data-id="element-1890">
              {rejected.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-1891">
              Rejected
            </p>
          </div>
        </div>

        <div className="space-y-6 mb-6" data-id="element-1892">
          {pending.length > 0 && <div data-id="element-1893">
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-1894">
                <Sparkles className="w-4 h-4 text-brand-500" data-id="element-1895" /> Live bids
              </h4>
              <div className="space-y-3" data-id="element-1896">
                {pending.map(b => <div key={b.originalIndex} className={`p-3 rounded-2xl border ${b.originalIndex === bestPending?.originalIndex ? 'bg-brand-50 border-brand-200' : 'bg-white border-gray-200'}`} data-id="element-1897">
                    <div className="flex justify-between items-start mb-3" data-id="element-1898">
                      <div className="flex items-center gap-3" data-id="element-1899">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs ${b.color}`} data-id="element-1900">
                          {b.initials}
                        </div>
                        <div data-id="element-1901">
                          <p className="text-sm font-bold text-gray-900" data-id="element-1902">
                            {b.supplier}
                          </p>
                          <p className="text-[10px] text-gray-500" data-id="element-1903">
                            {b.rating} ★ •{' '}
                            {new Date(b.receivedAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-1904">
                        <p className="font-display font-bold text-gray-900" data-id="element-1905">
                          AED {b.price}
                        </p>
                        {b.originalIndex === bestPending?.originalIndex && <span className="text-[9px] font-bold uppercase tracking-wider bg-brand-500 text-white px-1.5 py-0.5 rounded" data-id="element-1906">
                            Best
                          </span>}
                      </div>
                    </div>
                    <div className="flex gap-2" data-id="element-1907">
                      <button onClick={() => onReject(job.id, b.originalIndex)} className="flex-1 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-bold" data-id="element-1908">
                        Reject
                      </button>
                      <button onClick={() => {
                  onAccept(job.id, b.originalIndex);
                  onClose();
                }} className="flex-1 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold" data-id="element-1909">
                        Accept
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {accepted.length > 0 && <div data-id="element-1910">
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-1911">
                <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-1912" /> Accepted
              </h4>
              {accepted.map(b => <div key={b.originalIndex} className="p-3 rounded-2xl bg-green-50 border border-green-100 flex justify-between items-center opacity-80" data-id="element-1913">
                  <div className="flex items-center gap-3" data-id="element-1914">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-[10px] ${b.color}`} data-id="element-1915">
                      {b.initials}
                    </div>
                    <p className="text-sm font-bold text-gray-900" data-id="element-1916">
                      {b.supplier}
                    </p>
                  </div>
                  <p className="font-display font-bold text-gray-900" data-id="element-1917">
                    AED {b.price}
                  </p>
                </div>)}
            </div>}
        </div>

        <button onClick={onCancel} className="w-full py-3.5 text-rose-600 bg-rose-50 rounded-xl font-bold text-sm" data-id="element-1918">
          Cancel request
        </button>
      </div>
    </BottomSheet>;
}
function CancelSheet({
  job,
  onClose,
  onConfirm
}: {
  job: MobileJobRecord;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return <BottomSheet onClose={onClose} data-id="element-1919">
      <div className="px-6 pb-6 text-center" data-id="element-1920">
        <div className="w-16 h-16 rounded-3xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-4" data-id="element-1921">
          <AlertCircle className="w-8 h-8" data-id="element-1922" />
        </div>
        <h3 className="text-xl font-display font-bold text-gray-900 mb-2" data-id="element-1923">
          Cancel this {job.type === 'bidding' ? 'bidding' : 'job'}?
        </h3>
        <p className="text-sm text-gray-500 mb-6" data-id="element-1924">
          {job.type === 'bidding' ? "We'll notify the carrier network that the request is closed." : 'A cancellation fee may apply if the driver is already on the way.'}
        </p>
        <div className="bg-gray-50 rounded-2xl p-4 mb-6 text-left" data-id="element-1925">
          <p className="font-bold text-gray-900 text-sm mb-1" data-id="element-1926">
            {job.serviceName}
          </p>
          <p className="text-xs text-gray-500" data-id="element-1927">{job.routeHeadline}</p>
        </div>
        <div className="flex gap-3" data-id="element-1928">
          <button onClick={onClose} className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm" data-id="element-1929">
            Keep it
          </button>
          <button onClick={onConfirm} className="flex-1 py-3.5 bg-rose-500 text-white rounded-xl font-bold text-sm" data-id="element-1930">
            Yes, cancel
          </button>
        </div>
      </div>
    </BottomSheet>;
}
const SERVICE_ICON_MAP: Record<string, typeof HomeIcon> = {
  recovery: Truck,
  home: HomeIcon,
  furniture: Sofa,
  delivery: Package,
  reefer: Snowflake,
  equipment: Wrench
};
const SERVICE_COLOR_MAP: Record<string, string> = {
  recovery: 'bg-red-100 text-red-600',
  home: 'bg-emerald-100 text-emerald-600',
  furniture: 'bg-sky-100 text-sky-600',
  delivery: 'bg-amber-100 text-amber-600',
  reefer: 'bg-cyan-100 text-cyan-600',
  equipment: 'bg-fuchsia-100 text-fuchsia-600'
};
function formatThreadTime(ts: number): string {
  const now = new Date();
  const d = new Date(ts);
  const diffMs = now.getTime() - ts;
  const isToday = now.toDateString() === d.toDateString();
  if (isToday) {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (yesterday.toDateString() === d.toDateString()) return 'Yesterday';
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays < 7) return d.toLocaleDateString(undefined, {
    weekday: 'short'
  });
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short'
  });
}
function ChatThreadsList({
  chats,
  onResume,
  onCompose
}: {
  chats: ChatSession[];
  onResume: (chat: ChatSession) => void;
  onCompose: () => void;
}) {
  const sortedChats = [...chats].sort((a, b) => b.updatedAt - a.updatedAt);
  const unreadCount = chats.filter(c => c.unread).length;
  return <div className="flex flex-col h-full bg-white relative" data-id="element-1931">
      {/* Header */}
      <div className="px-5 py-3 border-b border-gray-100 bg-white shrink-0 z-10 sticky top-0" data-id="element-1932">
        <div className="flex items-center justify-between" data-id="element-1933">
          <div data-id="element-1934">
            <h1 className="text-xl font-display font-bold text-gray-900 tracking-tight" data-id="element-1935">
              Chats
            </h1>
            <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-1936">
              {chats.length} thread{chats.length === 1 ? '' : 's'}
              {unreadCount > 0 && ` · ${unreadCount} unread`}
            </p>
          </div>
          <button onClick={onCompose} className="w-10 h-10 rounded-full orange-gradient-bg text-white flex items-center justify-center shadow-md shadow-brand-500/30 active:scale-95 transition-transform" title="New chat" aria-label="New chat" data-id="element-1937">
            <Plus className="w-5 h-5" data-id="element-1938" />
          </button>
        </div>
      </div>

      {/* Threads */}
      <div className="flex-1 overflow-y-auto scrollbar-hide pb-24" data-id="element-1939">
        {sortedChats.length === 0 ? <div className="px-6 py-16 text-center" data-id="element-1940">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 text-gray-400 mx-auto mb-3 flex items-center justify-center" data-id="element-1941">
              <MessageCircle className="w-7 h-7" data-id="element-1942" />
            </div>
            <p className="text-sm font-bold text-gray-900" data-id="element-1943">No chats yet</p>
            <p className="text-xs text-gray-500 mt-1 mb-5 max-w-[220px] mx-auto" data-id="element-1944">
              Start a chat to find the right supplier at the best price.
            </p>
            <button onClick={onCompose} className="inline-flex items-center gap-1.5 px-4 py-2 orange-gradient-bg text-white rounded-full text-xs font-bold active:scale-95 transition-transform" data-id="element-1945">
              <Plus className="w-3.5 h-3.5" data-id="element-1946" />
              New chat
            </button>
          </div> : <div data-id="element-1947">
            {sortedChats.map((c, idx) => {
          const Icon = SERVICE_ICON_MAP[c.serviceId] || MessageCircle;
          const colorClass = SERVICE_COLOR_MAP[c.serviceId] || 'bg-gray-100 text-gray-600';
          const expired = Date.now() - c.createdAt > TWENTY_FOUR_HOURS;
          return <motion.button key={c.id} initial={{
            opacity: 0,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.04
          }} onClick={() => onResume(c)} className={`w-full px-5 py-3 flex items-start gap-3 transition-colors text-left ${idx !== sortedChats.length - 1 ? 'border-b border-gray-100' : ''} hover:bg-gray-50 active:bg-gray-100`} data-id="element-1948">
                  <div className="relative shrink-0" data-id="element-1949">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colorClass}`} data-id="element-1950">
                      <Icon className="w-5 h-5" data-id="element-1951" />
                    </div>
                    {c.unread && <span className="absolute top-0 right-0 w-3 h-3 rounded-full bg-brand-500 ring-2 ring-white" data-id="element-1952" />}
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-1953">
                    <div className="flex items-start justify-between gap-2 mb-0.5" data-id="element-1954">
                      <p className={`text-[14px] truncate ${c.unread ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`} data-id="element-1955">
                        {c.summary}
                      </p>
                      <span className={`text-[10px] shrink-0 whitespace-nowrap mt-0.5 ${c.unread ? 'text-brand-600 font-bold' : 'text-gray-400 font-medium'}`} data-id="element-1956">
                        {formatThreadTime(c.updatedAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1957">
                      <p className={`text-[12px] truncate flex-1 ${c.unread ? 'text-gray-700 font-medium' : 'text-gray-500'}`} data-id="element-1958">
                        {c.lastMessage}
                      </p>
                      {c.status === 'booked' && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 shrink-0" data-id="element-1959">
                          Booked
                        </span>}
                      {c.status === 'expired' || expired ? <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0 inline-flex items-center gap-0.5" data-id="element-1960">
                          <AlertTriangle className="w-2.5 h-2.5" data-id="element-1961" />
                          Expired
                        </span> : null}
                    </div>
                  </div>
                </motion.button>;
        })}
          </div>}
      </div>

      {/* Floating compose button (always available) */}
      {sortedChats.length > 0 && <button onClick={onCompose} className="absolute bottom-5 right-5 w-14 h-14 rounded-full orange-gradient-bg text-white flex items-center justify-center shadow-xl shadow-brand-500/40 active:scale-95 transition-transform z-20" aria-label="New chat" data-id="element-1962">
          <Plus className="w-6 h-6" data-id="element-1963" />
        </button>}
    </div>;
}
function NewChatPrompt({
  onSubmit,
  onBack
}: {
  onSubmit: (text: string) => void;
  onBack: () => void;
}) {
  const [input, setInput] = useState('');
  const submit = (text: string) => {
    const v = text.trim();
    if (!v) return;
    onSubmit(v);
  };
  return <div className="flex flex-col h-full bg-gray-50 relative" data-id="element-1964">
      <div className="px-3 py-3 border-b border-gray-100 bg-white/95 backdrop-blur-md shrink-0 z-10 sticky top-0" data-id="element-1965">
        <div className="flex items-center gap-2" data-id="element-1966">
          <button onClick={onBack} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-700 transition-colors shrink-0" aria-label="Back" data-id="element-1967">
            <ChevronLeft className="w-5 h-5" data-id="element-1968" />
          </button>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shrink-0" data-id="element-1969">
            <Sparkles className="w-4 h-4 text-white" data-id="element-1970" />
          </div>
          <div className="min-w-0" data-id="element-1971">
            <p className="font-bold text-gray-900 text-[13px] leading-tight truncate" data-id="element-1972">
              New chat
            </p>
            <p className="text-[10px] text-brand-600 font-medium truncate" data-id="element-1973">
              Pair2 AI · ready to help
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-5 scrollbar-hide pb-32" data-id="element-1974">
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex gap-2 max-w-[92%]" data-id="element-1975">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shrink-0 mt-1" data-id="element-1976">
            <Sparkles className="w-3.5 h-3.5 text-white" data-id="element-1977" />
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3.5 py-3 shadow-sm" data-id="element-1978">
            <p className="text-[13px] text-gray-800 leading-relaxed" data-id="element-1979">
              Hi 👋 I'm your Pair2 AI assistant.
            </p>
            <p className="text-[13px] text-gray-800 leading-relaxed mt-1.5" data-id="element-1980">
              Tell me what service you need and I'll find the right supplier at
              the best price.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="pl-9" data-id="element-1981">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" data-id="element-1982">
            Quick start
          </p>
          <div className="flex flex-wrap gap-1.5" data-id="element-1983">
            {DISCOVERY_CHIPS.map(c => {
            const Icon = c.icon;
            return <button key={c.label} onClick={() => submit(c.label)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-brand-200 text-brand-700 rounded-full text-[11px] font-medium shadow-sm active:scale-95 transition-transform" data-id="element-1984">
                  <Icon className="w-3 h-3" data-id="element-1985" />
                  {c.label}
                </button>;
          })}
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 p-3 pb-safe" data-id="element-1986">
        <div className="relative flex items-center gap-2" data-id="element-1987">
          <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') submit(input);
        }} placeholder="What service do you need?" className="flex-1 bg-gray-100 border-transparent rounded-full py-2.5 px-4 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" autoFocus data-id="element-1988" />
          <button onClick={() => submit(input)} disabled={!input.trim()} className="w-9 h-9 rounded-full bg-brand-500 text-white flex items-center justify-center disabled:opacity-50 transition-opacity shrink-0" data-id="element-1989">
            <Send className="w-4 h-4 -ml-0.5" data-id="element-1990" />
          </button>
        </div>
      </div>
    </div>;
}
function AgenticChatTab({
  chatId,
  serviceId,
  initialPrompt,
  onBack,
  onNewChat,
  onBook
}: {
  chatId: string;
  serviceId: ServiceId;
  initialPrompt: string;
  onBack: () => void;
  onNewChat: () => void;
  onBook: (job: MobileJobRecord) => void;
}) {
  const service = getServiceConfig(serviceId);
  const slots = getSlotsForService(serviceId);
  const turnIdRef = useRef(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bidIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chatExpired = Date.now() - chat.createdAt > TWENTY_FOUR_HOURS;
  const [thinking, setThinking] = useState(true);
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [values, setValues] = useState<SlotValues>({});
  const [activeSlot, setActiveSlot] = useState<Slot | null>(null);
  const [input, setInput] = useState('');
  const [isDone, setIsDone] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  // Offer stage state
  const [offerStage, setOfferStage] = useState<OfferStage>('offers');
  const [counterSheetFor, setCounterSheetFor] = useState<CarrierOffer | null>(null);
  const [biddingSheetOpen, setBiddingSheetOpen] = useState(false);
  const [counterPrice, setCounterPrice] = useState('');
  const [counterNote, setCounterNote] = useState('');
  const [counterRound, setCounterRound] = useState(0);
  const [negotiationCarrier, setNegotiationCarrier] = useState<CarrierOffer | null>(null);
  const [carrierCounterPrice, setCarrierCounterPrice] = useState<number | null>(null);
  const [bidBudget, setBidBudget] = useState('');
  const [bidWindow, setBidWindow] = useState('15');
  const [liveBids, setLiveBids] = useState<BidEntry[]>([]);
  const [confirmedBooking, setConfirmedBooking] = useState<{
    carrier: string;
    price: number;
    tripId: string;
    eta: string;
  } | null>(null);
  // Boot conversation — progressive: user msg → thinking → AI greeting
  useEffect(() => {
    const promptToUse = initialPrompt || service.defaultPrompt;
    const prefilled = parseInitialPrompt(promptToUse, service.id);
    const cleaned: SlotValues = {
      ...prefilled
    };
    setValues(cleaned);
    // Step 1: show user message immediately
    if (initialPrompt) {
      const userTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'user',
        text: promptToUse
      };
      setTurns([userTurn]);
    } else {
      setTurns([]);
    }
    setThinking(true);
    // Step 2: after typing delay, AI responds
    const t1 = setTimeout(() => {
      const greetingText = getInitialGreeting(service.id, promptToUse, cleaned, slots);
      const next = nextMissingSlot(cleaned, slots);
      let aiText = greetingText;
      let suggestions: string[] | undefined;
      let showUploader = false;
      let isComplete = false;
      if (next) {
        aiText += `\n\n${next.askPhrases[0]}`;
        suggestions = next.suggestions;
        showUploader = next.kind === 'photos';
      } else {
        aiText = getCompletionMessage(service.id);
        isComplete = true;
      }
      const aiTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'ai',
        text: aiText,
        suggestions,
        showUploader,
        isComplete
      };
      setTurns(prev => [...prev, aiTurn]);
      setActiveSlot(next);
      setThinking(false);
      if (!next) setIsDone(true);
    }, 1100);
    return () => clearTimeout(t1);
  }, [service.id, initialPrompt]);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, thinking, offerStage, liveBids]);
  const submitReply = (rawText: string) => {
    if (!rawText.trim() || !activeSlot) return;
    if (activeSlot.kind === 'photos') {
      const skipPattern = /^(no|nope|skip|none|no photo|no photos|no pictures|no pics|don'?t have|not now|later|pass)\b/i;
      if (skipPattern.test(rawText.trim())) {
        const nextValues = {
          ...values,
          [activeSlot.id]: ['done']
        };
        setValues(nextValues);
        const upcoming = nextMissingSlot(nextValues, slots);
        advanceConversation(rawText, nextValues, upcoming, '✓ Skipped photos.');
        return;
      }
    }
    const parsed = parseUserReply(rawText, activeSlot);
    const nextValues = {
      ...values,
      ...parsed
    };
    if ('__bonus_location__' in parsed) {
      const bonusValue = parsed['__bonus_location__'];
      delete nextValues['__bonus_location__'];
      const nextLocSlot = slots.find(s => s.kind === 'location' && s.id !== activeSlot.id && (nextValues[s.id] === undefined || nextValues[s.id] === ''));
      if (nextLocSlot) {
        nextValues[nextLocSlot.id] = bonusValue;
      }
    }
    setValues(nextValues);
    const ack = parsed[activeSlot.id] !== undefined ? formatAcknowledgement(activeSlot, parsed[activeSlot.id]) : '';
    const nextSlot = nextMissingSlot(nextValues, slots);
    advanceConversation(rawText, nextValues, nextSlot, ack);
    setInput('');
  };
  const advanceConversation = (userText: string, nextValues: SlotValues, nextSlot: Slot | null, ack: string) => {
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: userText
    };
    let aiText = ack;
    let suggestions: string[] | undefined;
    let showUploader = false;
    let isComplete = false;
    if (nextSlot) {
      aiText += (aiText ? '\n\n' : '') + nextSlot.askPhrases[0];
      suggestions = nextSlot.suggestions;
      showUploader = nextSlot.kind === 'photos';
    } else {
      aiText += (aiText ? '\n\n' : '') + getCompletionMessage(service.id);
      isComplete = true;
    }
    const aiTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'ai',
      text: aiText,
      suggestions,
      showUploader,
      isComplete
    };
    setTurns(prev => [...prev, userTurn]);
    setThinking(true);
    setTimeout(() => {
      setTurns(prev => [...prev, aiTurn]);
      setActiveSlot(nextSlot);
      setThinking(false);
      if (!nextSlot) setIsDone(true);
    }, 700);
  };
  const handlePhotoUpload = () => {
    if (!activeSlot || activeSlot.kind !== 'photos') return;
    const newCount = photoCount + 1;
    setPhotoCount(newCount);
    const photoUrl = `https://images.unsplash.com/photo-${1556909114 + newCount * 1000}-f6e7ad7d3136?w=300&h=200&fit=crop`;
    const attachments = [{
      url: photoUrl,
      type: 'image' as const
    }];
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: `📷 Photo ${newCount} uploaded`,
      attachments
    };
    setTurns(prev => [...prev, userTurn]);
    if (newCount >= 2) {
      const nextValues = {
        ...values,
        [activeSlot.id]: ['photo1', 'photo2']
      };
      setValues(nextValues);
      const upcoming = nextMissingSlot(nextValues, slots);
      setThinking(true);
      setTimeout(() => {
        let aiText = `✓ Got ${newCount} photos.`;
        let suggestions: string[] | undefined;
        let showUploader = false;
        let isComplete = false;
        if (upcoming) {
          aiText += `\n\n${upcoming.askPhrases[0]}`;
          suggestions = upcoming.suggestions;
          showUploader = upcoming.kind === 'photos';
        } else {
          aiText += `\n\n${getCompletionMessage(service.id)}`;
          isComplete = true;
        }
        const aiTurn: ConversationTurn = {
          id: turnIdRef.current++,
          role: 'ai',
          text: aiText,
          suggestions,
          showUploader,
          isComplete
        };
        setTurns(prev => [...prev, aiTurn]);
        setActiveSlot(upcoming);
        setThinking(false);
        if (!upcoming) setIsDone(true);
      }, 700);
    }
  };
  const confirmBooking = (carrier: string, price: number, eta: string) => {
    const tripId = `TRP-${Math.floor(10000 + Math.random() * 90000)}`;
    const newJob: MobileJobRecord = {
      id: tripId,
      type: 'booking',
      status: 'active',
      serviceId: service.id,
      serviceName: service.name,
      prompt: initialPrompt,
      routeHeadline: `${typeof values.pickup === 'string' ? values.pickup : 'Pickup'} → ${typeof values.dropoff === 'string' ? values.dropoff : 'Dropoff'}`,
      pickup: typeof values.pickup === 'string' ? values.pickup : 'Pickup location',
      dropoff: typeof values.dropoff === 'string' ? values.dropoff : 'Dropoff location',
      supplierName: carrier,
      price,
      createdAt: Date.now(),
      startedAt: Date.now()
    };
    onBook(newJob);
    setConfirmedBooking({
      carrier,
      price,
      tripId,
      eta
    });
    setOfferStage('confirmed');
    const aiTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'ai',
      text: `🎉 Booked with ${carrier} at AED ${price}. Trip ${tripId} created.`
    };
    setTurns(prev => [...prev, aiTurn]);
  };
  const openCounterSheet = (offer: CarrierOffer) => {
    if (chatExpired) return;
    setCounterSheetFor(offer);
    setCounterPrice(String(Math.round(offer.price * 0.85)));
    setCounterNote('');
  };
  const handleSendCounter = () => {
    if (!counterSheetFor || !counterPrice) return;
    const targetPrice = parseInt(counterPrice);
    const carrier = counterSheetFor;
    setNegotiationCarrier(carrier);
    const round = counterRound + 1;
    setCounterRound(round);
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: `Counter offer to ${carrier.name}: AED ${targetPrice}${counterNote ? ' — ' + counterNote : ''}`
    };
    setTurns(prev => [...prev, userTurn]);
    setCounterSheetFor(null);
    setOfferStage('counter-waiting');
    setThinking(true);
    setTimeout(() => {
      const sentTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'ai',
        text: `📡 Counter sent to ${carrier.name} (AED ${targetPrice}). Waiting for their response…`
      };
      setTurns(prev => [...prev, sentTurn]);
    }, 400);
    setTimeout(() => {
      setThinking(false);
      const outcome = round >= 3 ? Math.random() < 0.7 ? 'accept' : 'decline' : Math.random() < 0.4 ? 'accept' : Math.random() < 0.75 ? 'counter' : 'decline';
      if (outcome === 'accept') {
        const acceptTurn: ConversationTurn = {
          id: turnIdRef.current++,
          role: 'ai',
          text: `✅ ${carrier.name} accepted your counter of AED ${targetPrice}. Tap to confirm the booking.`
        };
        setTurns(prev => [...prev, acceptTurn]);
        setCarrierCounterPrice(targetPrice);
        setOfferStage('counter-response');
      } else if (outcome === 'counter') {
        const midpoint = Math.round((targetPrice + carrier.price) / 2);
        const counterTurn: ConversationTurn = {
          id: turnIdRef.current++,
          role: 'ai',
          text: `↩️ ${carrier.name} sent another counter offer: AED ${midpoint}. ${round >= 2 ? 'Last round — accept or walk away.' : 'Accept, counter again, or back out.'}`
        };
        setTurns(prev => [...prev, counterTurn]);
        setCarrierCounterPrice(midpoint);
        setOfferStage('counter-response');
      } else {
        const declineTurn: ConversationTurn = {
          id: turnIdRef.current++,
          role: 'ai',
          text: `❌ ${carrier.name} couldn't accept that price. Pick another carrier or open the job for bidding.`
        };
        setTurns(prev => [...prev, declineTurn]);
        setOfferStage('offers');
        setNegotiationCarrier(null);
        setCounterRound(0);
        setCarrierCounterPrice(null);
      }
    }, 2600);
  };
  const acceptCarrierCounter = () => {
    if (!negotiationCarrier || carrierCounterPrice == null) return;
    confirmBooking(negotiationCarrier.name, carrierCounterPrice, negotiationCarrier.eta);
  };
  const counterAgain = () => {
    if (!negotiationCarrier || carrierCounterPrice == null) return;
    setCounterSheetFor(negotiationCarrier);
    setCounterPrice(String(Math.max(carrierCounterPrice - 20, 50)));
    setCounterNote('');
  };
  const cancelNegotiation = () => {
    setNegotiationCarrier(null);
    setCounterRound(0);
    setCarrierCounterPrice(null);
    setOfferStage('offers');
    const cancelTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'ai',
      text: "Back to all offers. Pick a carrier when you're ready."
    };
    setTurns(prev => [...prev, cancelTurn]);
  };
  const handleStartBidding = () => {
    if (!bidBudget) return;
    setBiddingSheetOpen(false);
    setOfferStage('bidding-live');
    setLiveBids([]);
    const userTurn: ConversationTurn = {
      id: turnIdRef.current++,
      role: 'user',
      text: `Open for bidding · Max AED ${bidBudget} · ${bidWindow} min window`
    };
    setTurns(prev => [...prev, userTurn]);
    setTimeout(() => {
      const liveTurn: ConversationTurn = {
        id: turnIdRef.current++,
        role: 'ai',
        text: '🔴 Your job is live on the load board. Carriers are bidding now…'
      };
      setTurns(prev => [...prev, liveTurn]);
    }, 350);
    let count = 0;
    const max = parseInt(bidBudget);
    const usedCarriers = new Set<string>();
    bidIntervalRef.current = setInterval(() => {
      count++;
      const available = BID_CARRIERS.filter(c => !usedCarriers.has(c.name));
      if (available.length === 0) return;
      const pick = available[Math.floor(Math.random() * available.length)];
      usedCarriers.add(pick.name);
      const newBid: BidEntry = {
        id: `bid-${count}`,
        carrier: pick.name,
        rating: pick.rating,
        price: Math.max(max - Math.floor(40 + Math.random() * 80) - count * 8, Math.round(max * 0.6))
      };
      setLiveBids(prev => [newBid, ...prev]);
      if (count >= 4) {
        if (bidIntervalRef.current) clearInterval(bidIntervalRef.current);
        setTimeout(() => {
          setOfferStage('bidding-complete');
          const completeTurn: ConversationTurn = {
            id: turnIdRef.current++,
            role: 'ai',
            text: '✅ Bidding closed. Review the offers below — accept the one you like or counter any of them.'
          };
          setTurns(prev => [...prev, completeTurn]);
        }, 1200);
      }
    }, 1400);
  };
  useEffect(() => () => {
    if (bidIntervalRef.current) clearInterval(bidIntervalRef.current);
  }, []);
  const acceptBid = (bid: BidEntry) => {
    confirmBooking(bid.carrier, bid.price, '20 min');
  };
  const counterBid = (bid: BidEntry) => {
    if (chatExpired) return;
    const pseudoOffer: CarrierOffer = {
      id: bid.id,
      name: bid.carrier,
      rating: bid.rating,
      reviews: 0,
      vehicle: 'Carrier vehicle',
      price: bid.price,
      eta: '20 min'
    };
    setCounterSheetFor(pseudoOffer);
    setCounterPrice(String(Math.max(Math.round(bid.price * 0.9), 50)));
    setCounterNote('');
  };
  return <div className="flex flex-col h-full bg-gray-50 relative" data-id="element-1991">
      {/* Header */}
      <div className="px-3 py-3 border-b border-gray-100 bg-white/95 backdrop-blur-md shrink-0 z-10 sticky top-0" data-id="element-1992">
        <div className="flex items-center justify-between gap-2" data-id="element-1993">
          <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-1994">
            <button onClick={onBack} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-700 transition-colors shrink-0" aria-label="Back to chats" data-id="element-1995">
              <ChevronLeft className="w-5 h-5" data-id="element-1996" />
            </button>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${SERVICE_COLOR_MAP[serviceId] || 'bg-gray-100 text-gray-600'}`} data-id="element-1997">
              {(() => {
              const Icon = SERVICE_ICON_MAP[serviceId] || MessageCircle;
              return <Icon className="w-4 h-4" data-id="element-1998" />;
            })()}
            </div>
            <div className="min-w-0" data-id="element-1999">
              <p className="font-bold text-gray-900 text-[13px] leading-tight truncate" data-id="element-2000">
                {service.name}
              </p>
              <p className="text-[10px] text-brand-600 font-medium truncate" data-id="element-2001">
                Pair2 AI · {formatRelativeTime(chat.createdAt)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0" data-id="element-2002">
            {!isDone && <div className="flex items-center gap-1" data-id="element-2003">
                {slots.filter(s => !s.optional).map(s => {
              const isFilled = values[s.id] !== undefined && values[s.id] !== '';
              return <div key={s.id} className={`w-1.5 h-1.5 rounded-full ${isFilled ? 'bg-brand-500' : 'bg-gray-200'}`} data-id="element-2004" />;
            })}
              </div>}
            <button onClick={onNewChat} className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center transition-colors" title="New chat" aria-label="New chat" data-id="element-2005">
              <Plus className="w-4 h-4" data-id="element-2006" />
            </button>
          </div>
        </div>
        {chatExpired && <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-2007">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" data-id="element-2008" />
            <div data-id="element-2009">
              <p className="text-[11px] font-bold text-amber-800 leading-tight" data-id="element-2010">
                This chat is over 24 hours old
              </p>
              <p className="text-[10px] text-amber-700 leading-tight mt-0.5" data-id="element-2011">
                Quotes have expired. Start a new chat to book.
              </p>
            </div>
          </div>}
      </div>

      {/* Chat Area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-hide pb-32" data-id="element-2012">
        {turns.map(m => m.role === 'ai' ? <div key={m.id} className="flex gap-2 max-w-[90%]" data-id="element-2013">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shrink-0 mt-1" data-id="element-2014">
                <Sparkles className="w-3 h-3 text-white" data-id="element-2015" />
              </div>
              <div className="space-y-2" data-id="element-2016">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm" data-id="element-2017">
                  <p className="text-[13px] text-gray-800 leading-relaxed whitespace-pre-wrap" data-id="element-2018">
                    {m.text}
                  </p>
                </div>
                {m.showUploader && !isDone && <button onClick={handlePhotoUpload} className="flex items-center justify-center gap-2 w-full py-2.5 bg-white border border-dashed border-gray-300 rounded-xl text-brand-600 text-xs font-bold hover:bg-brand-50 transition-colors" data-id="element-2019">
                    <Camera className="w-4 h-4" data-id="element-2020" />
                    Take Photo
                  </button>}
                {m.suggestions && !isDone && <div className="flex flex-wrap gap-1.5 pt-1" data-id="element-2021">
                    {m.suggestions.map(s => <button key={s} onClick={() => submitReply(s)} className="px-3 py-1.5 bg-white border border-brand-200 text-brand-700 rounded-full text-[11px] font-medium shadow-sm active:scale-95 transition-transform" data-id="element-2022">
                        {s}
                      </button>)}
                  </div>}
              </div>
            </div> : <div key={m.id} className="flex justify-end" data-id="element-2023">
              <div className="space-y-2 max-w-[85%]" data-id="element-2024">
                {m.text && <div className="bg-brand-500 rounded-2xl rounded-tr-sm px-3.5 py-2.5 shadow-sm" data-id="element-2025">
                    <p className="text-[13px] text-white leading-relaxed" data-id="element-2026">
                      {m.text}
                    </p>
                  </div>}
                {m.attachments?.map((a, i) => <img key={i} src={a.url} alt="Upload" className="w-40 h-32 object-cover rounded-xl border-2 border-white shadow-sm" data-id="element-2027" />)}
              </div>
            </div>)}

        {thinking && <div className="flex gap-2 max-w-[90%]" data-id="element-2028">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shrink-0 mt-1" data-id="element-2029">
              <Sparkles className="w-3 h-3 text-white" data-id="element-2030" />
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm flex items-center gap-1.5" data-id="element-2031">
              <motion.span className="w-1.5 h-1.5 rounded-full bg-gray-400" animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: 0
          }} data-id="element-2032" />
              <motion.span className="w-1.5 h-1.5 rounded-full bg-gray-400" animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: 0.15
          }} data-id="element-2033" />
              <motion.span className="w-1.5 h-1.5 rounded-full bg-gray-400" animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: 0.3
          }} data-id="element-2034" />
            </div>
          </div>}

        {/* Offers + Negotiation + Bidding + Confirmation */}
        {isDone && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="pt-4 space-y-4" data-id="element-2035">
            {/* OFFERS LIST */}
            {offerStage === 'offers' && <>
                <div className="flex items-center gap-2 px-2" data-id="element-2036">
                  <div className="h-px flex-1 bg-gray-200" data-id="element-2037" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2038">
                    {MOCK_OFFERS.length} Carriers Available
                  </span>
                  <div className="h-px flex-1 bg-gray-200" data-id="element-2039" />
                </div>

                {MOCK_OFFERS.map((offer, idx) => <motion.div key={offer.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.07
          }} className="bg-white rounded-2xl p-3.5 border border-gray-200 shadow-sm" data-id="element-2040">
                    <div className="flex justify-between items-start mb-2.5" data-id="element-2041">
                      <div className="min-w-0 flex-1" data-id="element-2042">
                        <div className="flex items-center gap-1.5 mb-0.5" data-id="element-2043">
                          <h4 className="font-bold text-gray-900 text-[13px] truncate" data-id="element-2044">
                            {offer.name}
                          </h4>
                          {offer.verified && <Shield className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-2045" />}
                          {offer.fast && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700" data-id="element-2046">
                              Fast
                            </span>}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500" data-id="element-2047">
                          <span className="flex items-center gap-0.5" data-id="element-2048">
                            <Star className="w-2.5 h-2.5 text-amber-400 fill-current" data-id="element-2049" />
                            {offer.rating} ({offer.reviews})
                          </span>
                          <span data-id="element-2050">·</span>
                          <span data-id="element-2051">{offer.vehicle}</span>
                          <span data-id="element-2052">·</span>
                          <span className="flex items-center gap-0.5" data-id="element-2053">
                            <Clock className="w-2.5 h-2.5" data-id="element-2054" />
                            {offer.eta}
                          </span>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-3" data-id="element-2055">
                        <p className="text-lg font-display font-bold text-gray-900 leading-none" data-id="element-2056">
                          AED {offer.price}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2" data-id="element-2057">
                      <button disabled={chatExpired} onClick={() => confirmBooking(offer.name, offer.price, offer.eta)} className="py-2 bg-gray-900 text-white rounded-xl text-xs font-bold active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2058">
                        Accept
                      </button>
                      <button disabled={chatExpired} onClick={() => openCounterSheet(offer)} className="py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2059">
                        Counter
                      </button>
                    </div>
                  </motion.div>)}

                {/* Open for Bidding card — last as alternative */}
                <div className="pt-1" data-id="element-2060">
                  <div className="flex items-center gap-2 px-2 mb-3" data-id="element-2061">
                    <div className="h-px flex-1 bg-gray-200" data-id="element-2062" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2063">
                      Or save more
                    </span>
                    <div className="h-px flex-1 bg-gray-200" data-id="element-2064" />
                  </div>
                  <button disabled={chatExpired} onClick={() => setBiddingSheetOpen(true)} className="w-full bg-gradient-to-br from-brand-500 to-brand-600 text-white rounded-2xl p-4 text-left shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2065">
                    <div className="flex items-start justify-between gap-3" data-id="element-2066">
                      <div data-id="element-2067">
                        <div className="flex items-center gap-1.5 mb-1" data-id="element-2068">
                          <ListChecks className="w-4 h-4" data-id="element-2069" />
                          <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-2070">
                            Smart Save
                          </span>
                        </div>
                        <h4 className="font-bold text-sm" data-id="element-2071">Open for Bidding</h4>
                        <p className="text-[11px] text-white/85 mt-0.5" data-id="element-2072">
                          Let carriers compete · save ~18%
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 mt-1" data-id="element-2073" />
                    </div>
                  </button>
                </div>
              </>}

            {/* COUNTER WAITING */}
            {offerStage === 'counter-waiting' && negotiationCarrier && <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm" data-id="element-2074">
                <div className="flex items-center gap-3" data-id="element-2075">
                  <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center shrink-0" data-id="element-2076">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-2077">
                      <Loader2 className="w-5 h-5 text-brand-500" data-id="element-2078" />
                    </motion.div>
                  </div>
                  <div className="min-w-0" data-id="element-2079">
                    <p className="text-sm font-bold text-gray-900" data-id="element-2080">
                      Negotiating…
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-2081">
                      Waiting on {negotiationCarrier.name}
                    </p>
                  </div>
                </div>
              </div>}

            {/* COUNTER RESPONSE */}
            {offerStage === 'counter-response' && negotiationCarrier && carrierCounterPrice != null && <div className="bg-white rounded-2xl p-4 border-2 border-brand-200 shadow-sm" data-id="element-2082">
                  <div className="flex items-center justify-between mb-3" data-id="element-2083">
                    <div data-id="element-2084">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600 mb-0.5" data-id="element-2085">
                        {negotiationCarrier.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2086">
                        Round {counterRound} of 3
                      </p>
                    </div>
                    <div className="text-right" data-id="element-2087">
                      <p className="text-[10px] text-gray-400 line-through" data-id="element-2088">
                        AED {negotiationCarrier.price}
                      </p>
                      <p className="text-xl font-display font-bold text-brand-600 leading-none" data-id="element-2089">
                        AED {carrierCounterPrice}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-2090">
                    <button disabled={chatExpired} onClick={acceptCarrierCounter} className="w-full py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2091">
                      Accept AED {carrierCounterPrice}
                    </button>
                    <div className="grid grid-cols-2 gap-2" data-id="element-2092">
                      <button disabled={counterRound >= 3 || chatExpired} onClick={counterAgain} className="py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 rounded-xl text-xs font-bold transition-colors" data-id="element-2093">
                        Counter again
                      </button>
                      <button onClick={cancelNegotiation} className="py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-bold hover:bg-gray-50 transition-colors" data-id="element-2094">
                        Back to offers
                      </button>
                    </div>
                  </div>
                </div>}

            {/* BIDDING LIVE */}
            {offerStage === 'bidding-live' && <div className="bg-white rounded-2xl p-4 border border-brand-200 shadow-sm" data-id="element-2095">
                <div className="flex items-center justify-between mb-4" data-id="element-2096">
                  <div className="flex items-center gap-2" data-id="element-2097">
                    <span className="relative flex h-2 w-2" data-id="element-2098">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" data-id="element-2099" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" data-id="element-2100" />
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-900" data-id="element-2101">
                      Live Bidding
                    </span>
                  </div>
                  <span className="text-[11px] text-gray-500 font-medium" data-id="element-2102">
                    {liveBids.length} bid
                    {liveBids.length === 1 ? '' : 's'} in
                  </span>
                </div>
                <div className="space-y-2" data-id="element-2103">
                  <AnimatePresence data-id="element-2104">
                    {liveBids.map(bid => <motion.div key={bid.id} initial={{
                opacity: 0,
                x: -20,
                scale: 0.95
              }} animate={{
                opacity: 1,
                x: 0,
                scale: 1
              }} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl" data-id="element-2105">
                        <div className="min-w-0" data-id="element-2106">
                          <p className="text-xs font-bold text-gray-900 truncate" data-id="element-2107">
                            {bid.carrier}
                          </p>
                          <p className="text-[10px] text-gray-500 flex items-center gap-0.5" data-id="element-2108">
                            <Star className="w-2.5 h-2.5 text-amber-400 fill-current" data-id="element-2109" />
                            {bid.rating}
                          </p>
                        </div>
                        <span className="text-sm font-bold text-gray-900 ml-2" data-id="element-2110">
                          AED {bid.price}
                        </span>
                      </motion.div>)}
                  </AnimatePresence>
                  {liveBids.length === 0 && <div className="text-center py-4 text-[11px] text-gray-400" data-id="element-2111">
                      Waiting for carriers to bid…
                    </div>}
                </div>
              </div>}

            {/* BIDDING COMPLETE — Accept or Counter on each bid */}
            {offerStage === 'bidding-complete' && <>
                <div className="flex items-center gap-2 px-2" data-id="element-2112">
                  <div className="h-px flex-1 bg-gray-200" data-id="element-2113" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600" data-id="element-2114">
                    Bidding closed · {liveBids.length} offers
                  </span>
                  <div className="h-px flex-1 bg-gray-200" data-id="element-2115" />
                </div>
                {[...liveBids].sort((a, b) => a.price - b.price).map((bid, idx) => <motion.div key={bid.id} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.06
          }} className={`bg-white rounded-2xl p-3.5 border shadow-sm ${idx === 0 ? 'border-2 border-emerald-400' : 'border-gray-200'}`} data-id="element-2116">
                      <div className="flex justify-between items-start mb-2.5" data-id="element-2117">
                        <div className="min-w-0" data-id="element-2118">
                          <div className="flex items-center gap-1.5" data-id="element-2119">
                            <h4 className="font-bold text-gray-900 text-[13px]" data-id="element-2120">
                              {bid.carrier}
                            </h4>
                            {idx === 0 && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" data-id="element-2121">
                                Best Price
                              </span>}
                          </div>
                          <p className="text-[10px] text-gray-500 flex items-center gap-0.5 mt-0.5" data-id="element-2122">
                            <Star className="w-2.5 h-2.5 text-amber-400 fill-current" data-id="element-2123" />
                            {bid.rating}
                          </p>
                        </div>
                        <p className="text-lg font-display font-bold text-gray-900" data-id="element-2124">
                          AED {bid.price}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-2" data-id="element-2125">
                        <button disabled={chatExpired} onClick={() => acceptBid(bid)} className={`py-2 rounded-xl text-xs font-bold active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed ${idx === 0 ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-gray-900 hover:bg-black text-white'}`} data-id="element-2126">
                          Accept
                        </button>
                        <button disabled={chatExpired} onClick={() => counterBid(bid)} className="py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2127">
                          Counter
                        </button>
                      </div>
                    </motion.div>)}
                <button onClick={() => {
            setOfferStage('offers');
            setLiveBids([]);
          }} className="w-full py-2 text-xs font-bold text-gray-500 hover:text-gray-700" data-id="element-2128">
                  ← Back to direct offers
                </button>
              </>}

            {/* CONFIRMED — booking success */}
            {offerStage === 'confirmed' && confirmedBooking && <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5 border border-emerald-200" data-id="element-2129">
                <div className="flex items-center gap-3 mb-4" data-id="element-2130">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 20
            }} className="w-11 h-11 rounded-full bg-emerald-500 flex items-center justify-center shrink-0" data-id="element-2131">
                    <CheckCircle2 className="w-6 h-6 text-white" data-id="element-2132" />
                  </motion.div>
                  <div data-id="element-2133">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700" data-id="element-2134">
                      Booking confirmed
                    </p>
                    <p className="text-base font-display font-bold text-gray-900" data-id="element-2135">
                      {confirmedBooking.tripId}
                    </p>
                  </div>
                </div>
                <div className="bg-white/70 rounded-xl p-3 mb-3 space-y-1.5" data-id="element-2136">
                  <div className="flex justify-between text-xs" data-id="element-2137">
                    <span className="text-gray-500" data-id="element-2138">Carrier</span>
                    <span className="font-bold text-gray-900" data-id="element-2139">
                      {confirmedBooking.carrier}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs" data-id="element-2140">
                    <span className="text-gray-500" data-id="element-2141">ETA</span>
                    <span className="font-bold text-gray-900" data-id="element-2142">
                      {confirmedBooking.eta}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs" data-id="element-2143">
                    <span className="text-gray-500" data-id="element-2144">Total</span>
                    <span className="font-bold text-gray-900" data-id="element-2145">
                      AED {confirmedBooking.price}
                    </span>
                  </div>
                </div>
                <button onClick={onBack} className="w-full py-2.5 bg-gray-900 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform" data-id="element-2146">
                  View in Chats
                  <ArrowRight className="w-4 h-4" data-id="element-2147" />
                </button>
              </motion.div>}
          </motion.div>}
      </div>

      {/* Bottom Sheets */}
      <AnimatePresence data-id="element-2148">
        {counterSheetFor && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setCounterSheetFor(null)} className="absolute inset-0 bg-black/40 backdrop-blur-sm z-40" data-id="element-2149" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 p-6 pb-safe" data-id="element-2150">
              <div className="flex items-start justify-between mb-1" data-id="element-2151">
                <div data-id="element-2152">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600" data-id="element-2153">
                    {counterRound > 0 ? `Round ${counterRound + 1} of 3` : 'New counter'}
                  </p>
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-2154">
                    Counter {counterSheetFor.name}
                  </h3>
                </div>
                <div className="text-right" data-id="element-2155">
                  <p className="text-[10px] text-gray-400" data-id="element-2156">Their price</p>
                  <p className="text-base font-display font-bold text-gray-900" data-id="element-2157">
                    AED {counterSheetFor.price}
                  </p>
                </div>
              </div>
              <div className="space-y-4 mt-4" data-id="element-2158">
                <div data-id="element-2159">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-2160">
                    Your price (AED)
                  </label>
                  <input type="number" value={counterPrice} onChange={e => setCounterPrice(e.target.value)} placeholder={`e.g. ${Math.round(counterSheetFor.price * 0.85)}`} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" autoFocus data-id="element-2161" />
                </div>
                <div data-id="element-2162">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-2163">
                    Note (optional)
                  </label>
                  <input type="text" value={counterNote} onChange={e => setCounterNote(e.target.value)} placeholder="e.g. I have flexibility on time" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-2164" />
                </div>
                <button onClick={handleSendCounter} disabled={!counterPrice} className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-bold disabled:opacity-50 active:scale-95 transition-transform" data-id="element-2165">
                  Send counter offer
                </button>
              </div>
            </motion.div>
          </>}

        {biddingSheetOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setBiddingSheetOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm z-40" data-id="element-2166" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 p-6 pb-safe" data-id="element-2167">
              <div className="flex items-center gap-2 mb-1" data-id="element-2168">
                <ListChecks className="w-4 h-4 text-brand-600" data-id="element-2169" />
                <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600" data-id="element-2170">
                  Smart save · ~18% lower
                </p>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2171">
                Open for bidding
              </h3>
              <p className="text-xs text-gray-500 mb-4" data-id="element-2172">
                Carriers will compete for your job. Pick the winner at the end.
              </p>
              <div className="space-y-4" data-id="element-2173">
                <div data-id="element-2174">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-2175">
                    Max budget (AED)
                  </label>
                  <input type="number" value={bidBudget} onChange={e => setBidBudget(e.target.value)} placeholder="e.g. 500" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" autoFocus data-id="element-2176" />
                </div>
                <div data-id="element-2177">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-2178">
                    Bidding window
                  </label>
                  <div className="flex gap-2" data-id="element-2179">
                    {['15', '30', '60'].map(w => <button key={w} onClick={() => setBidWindow(w)} className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${bidWindow === w ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-gray-200 text-gray-600'}`} data-id="element-2180">
                        {w} min
                      </button>)}
                  </div>
                </div>
                <button onClick={handleStartBidding} disabled={!bidBudget} className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-bold disabled:opacity-50 active:scale-95 transition-transform" data-id="element-2181">
                  Start bidding
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Composer */}
      {!isDone && <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 p-3 pb-safe" data-id="element-2182">
          <div className="relative flex items-center gap-2" data-id="element-2183">
            <button className="p-2 text-gray-400 hover:text-brand-500 transition-colors shrink-0" data-id="element-2184">
              <ImagePlus className="w-5 h-5" data-id="element-2185" />
            </button>
            <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') submitReply(input);
        }} placeholder="Type your reply..." className="flex-1 bg-gray-100 border-transparent rounded-full py-2.5 px-4 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-2186" />
            <button onClick={() => submitReply(input)} disabled={!input.trim()} className="w-9 h-9 rounded-full bg-brand-500 text-white flex items-center justify-center disabled:opacity-50 transition-opacity shrink-0" data-id="element-2187">
              <Send className="w-4 h-4 -ml-0.5" data-id="element-2188" />
            </button>
          </div>
        </div>}
    </div>;
}
function ProfileTab({
  onSignOut
}: {
  onSignOut: () => void;
}) {
  return <div className="px-6 pt-4 pb-8 space-y-5" data-id="element-2189">
      <h1 className="text-2xl font-display font-bold text-gray-900 tracking-tight" data-id="element-2190">
        Profile
      </h1>

      <div className="bg-white border border-gray-100 rounded-3xl p-5 flex items-center gap-4 shadow-sm" data-id="element-2191">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center font-bold text-xl text-white" data-id="element-2192">
          S
        </div>
        <div className="flex-1 min-w-0" data-id="element-2193">
          <p className="font-bold text-gray-900" data-id="element-2194">Sarah Khan</p>
          <p className="text-[10px] uppercase tracking-wider text-brand-600 font-bold inline-flex items-center gap-1 mt-0.5" data-id="element-2195">
            <Star className="w-3 h-3 fill-current" data-id="element-2196" /> Gold member
          </p>
        </div>
      </div>

      <Section title="Saved places" data-id="element-2197">
        <ProfileRow icon={HomeIcon} label="Home" sub="Marina Heights, T2" data-id="element-2198" />
        <ProfileRow icon={Briefcase} label="Work" sub="DIFC, Gate Village 4" data-id="element-2199" />
      </Section>

      <Section title="Payment" data-id="element-2200">
        <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex items-center gap-3" data-id="element-2201">
          <div className="w-10 h-10 rounded-lg bg-gray-900 text-white flex items-center justify-center" data-id="element-2202">
            <CreditCard className="w-4 h-4" data-id="element-2203" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-2204">
            <p className="text-sm font-bold text-gray-900" data-id="element-2205">Visa · 4421</p>
            <p className="text-[11px] text-gray-500" data-id="element-2206">Default · expires 09/27</p>
          </div>
          <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-2207" />
        </div>
      </Section>

      <Section title="Settings" data-id="element-2208">
        <ProfileRow icon={Bell} label="Notifications" data-id="element-2209" />
        <ProfileRow icon={Shield} label="Privacy" data-id="element-2210" />
        <ProfileRow icon={Settings} label="Preferences" data-id="element-2211" />
      </Section>

      <button onClick={onSignOut} className="w-full py-3 rounded-2xl bg-gray-50 hover:bg-gray-100 text-red-600 font-bold text-sm transition-colors flex items-center justify-center gap-2" data-id="element-2212">
        <LogOut className="w-4 h-4" data-id="element-2213" /> Sign out
      </button>
    </div>;
}
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-2214">
      <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-2" data-id="element-2215">
        {title}
      </h2>
      <div className="space-y-2" data-id="element-2216">{children}</div>
    </div>;
}
function ProfileRow({
  icon: Icon,
  label,
  sub
}: {
  icon: typeof Bell;
  label: string;
  sub?: string;
}) {
  return <button className="w-full bg-white border border-gray-100 rounded-xl p-3 shadow-sm flex items-center gap-3 hover:border-gray-200 transition-colors" data-id="element-2217">
      <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center" data-id="element-2218">
        <Icon className="w-4 h-4" data-id="element-2219" />
      </div>
      <div className="flex-1 min-w-0 text-left" data-id="element-2220">
        <p className="text-sm font-bold text-gray-900" data-id="element-2221">{label}</p>
        {sub && <p className="text-[11px] text-gray-500" data-id="element-2222">{sub}</p>}
      </div>
      <ArrowRight className="w-4 h-4 text-gray-400" data-id="element-2223" />
    </button>;
}